import { apiClient, getActivePvzId, isSupabaseConfigured } from './_shared'
import { getSalaryConfig, getSalaryConfigHistory, normalizeSalaryConfigData, getNextPayDayDate } from './salaryConfig'
import { getPayments } from './payments'
import { autoCloseExpiredShifts } from './schedule'

// ============================================
// AUTO SALARY CALCULATION
/**
 * Phase 5: Зарплатные периоды с Группами A/B.
 *
 * Периоды фиксированные: 1–15, 16–конец месяца.
 * Группа выплаты определяется по дню hire_date:
 *   Группа A: день 1-7 или 16-23
 *   Группа B: день 8-15 или 24-31
 *
 * Окна выплат:
 *   Группа A, период 1-15:    15-20 текущего месяца
 *   Группа A, период 16-end:  1-5 следующего месяца
 *   Группа B, период 1-15:    20-25 текущего месяца
 *   Группа B, период 16-end:  5-10 следующего месяца
 *
 * Дата выплаты = понедельник внутри окна. Если нет → ближайший ПН после окна.
 * В первый месяц — расчёт только с даты трудоустройства.
 */
/**
 * Вычисляет эффективную периодичность выплат для сотрудника.
 * Приоритет:
 * 1. Индивидуальный override сотрудника (payout_frequency_override)
 * 2. Если сотрудник подменный (employment_type === 'substitute') -> по умолчанию 'weekly'
 * 3. Общая периодичность ПВЗ (salaryConfig.payout_frequency || 'bi_monthly')
 */
export function getEffectivePayoutFrequency(employee, salaryConfig) {
  if (employee?.payout_frequency_override || employee?.payoutFrequencyOverride) {
    return employee.payout_frequency_override || employee.payoutFrequencyOverride
  }
  if (employee?.employment_type === 'substitute' || employee?.employmentType === 'substitute') {
    return 'weekly'
  }
  return salaryConfig?.payout_frequency || salaryConfig?.payoutFrequency || 'bi_monthly'
}

/**
 * Разрешает конфигурацию зарплат на целевую дату с учетом истории изменений.
 *
 * Инвариант: UNKNOWN HISTORY MUST NOT BECOME FAKE HISTORY.
 * Если для даты нет достоверного salary config (дата раньше первой известной записи или история отсутствует):
 * в режиме strictHistoricalCoverage=true система возвращает reliable: false и config: null.
 *
 * @param {string} targetDate - Дата смены (YYYY-MM-DD)
 * @param {Object} currentConfig - Действующая конфигурация тарифов
 * @param {Array} configHistory - Массив срезов истории [{ effective_from, config }]
 * @param {Object} [options={}]
 * @param {boolean} [options.strictHistoricalCoverage=false] - Включает строгий режим
 * @returns {{
 *   config: Object|null,
 *   reliable: boolean,
 *   reason?: string,
 *   effectiveFrom?: string|null,
 *   earliestReliableDate?: string|null
 * }}
 */
export function resolveSalaryConfigForDate(targetDate, currentConfig, configHistory = [], options = {}) {
  const strict = Boolean(options.strictHistoricalCoverage)
  const normalizedDate = typeof targetDate === 'string' ? targetDate.split('T')[0] : (targetDate ? new Date(targetDate).toISOString().split('T')[0] : '')

  const history = Array.isArray(configHistory) ? configHistory.filter(h => h && h.effective_from) : []

  if (history.length === 0) {
    if (strict) {
      return {
        config: null,
        reliable: false,
        reason: 'history_missing',
        effectiveFrom: null,
        earliestReliableDate: null
      }
    }
    return {
      config: currentConfig || null,
      reliable: false,
      reason: 'legacy_fallback_no_history',
      effectiveFrom: null,
      earliestReliableDate: null
    }
  }

  const sorted = [...history].sort((a, b) => b.effective_from.localeCompare(a.effective_from))
  const earliestReliableDate = sorted[sorted.length - 1].effective_from

  const matched = sorted.find(h => normalizedDate >= h.effective_from)

  if (matched) {
    return {
      config: matched.config || null,
      reliable: true,
      effectiveFrom: matched.effective_from,
      earliestReliableDate
    }
  }

  // targetDate раньше первой достоверной записи в истории
  if (strict) {
    return {
      config: null,
      reliable: false,
      reason: 'history_before_reliable_baseline',
      effectiveFrom: null,
      earliestReliableDate
    }
  }

  return {
    config: sorted[sorted.length - 1].config || null,
    reliable: false,
    reason: 'legacy_fallback_before_first_history',
    effectiveFrom: sorted[sorted.length - 1].effective_from,
    earliestReliableDate
  }
}

/**
 * Data Discovery Boundary для Daily Settlement:
 * Определяет дату, с которой необходимо считывать фактическую активность сотрудника (график, смены, выплаты, штрафы).
 * СТРОГО НЕ ВКЛЮЧАЕТ earliestReliableSalaryHistory, чтобы не терять исторические смены!
 *
 * Приоритет:
 * MAX(hired_at if known, settlementCutoverDate if known).
 * Если обе даты отсутствуют — используется fallbackEarliestDate (фактическая дата первой активности из БД), если передана.
 * Никаких угаданных дат (2026-01-01, 90 дней, текущий год и т.д.).
 * Если ни одной границы нет — возвращает null.
 */
export function getDailyDataDiscoveryStart(employee, salaryConfig, fallbackEarliestDate = null) {
  const empHire = employee?.hired_at ? employee.hired_at.split('T')[0] : null
  const cutover = salaryConfig?.settlementCutoverDate || salaryConfig?.settlement_cutover_date || null
  const fallback = typeof fallbackEarliestDate === 'string' ? fallbackEarliestDate.split('T')[0] : null

  const knownBoundaries = []
  if (empHire) knownBoundaries.push(empHire)
  if (cutover) knownBoundaries.push(cutover)

  if (knownBoundaries.length > 0) {
    return knownBoundaries.reduce((max, d) => (d > max ? d : max), knownBoundaries[0])
  }

  if (fallback) {
    return fallback
  }

  return null
}

/**
 * Authoritative Start Boundary:
 * Canonical Data Discovery Start для Daily (алиас к getDailyDataDiscoveryStart).
 * Больше НЕ обрезает смены по истории тарифов (earliestReliableSalaryHistory).
 */
export function getDailyAuthoritativeStart(employee, salaryConfig, fallbackOrHistory = null) {
  const fallback = typeof fallbackOrHistory === 'string' ? fallbackOrHistory : null
  return getDailyDataDiscoveryStart(employee, salaryConfig, fallback)
}

/**
 * Чистый хелпер для обнаружения кандидатных daily периодов сотрудника.
 * Находит фактические смены сотрудника в scheduleRows в рамках discovery boundary
 * и формирует список дневных периодов для последующего расчета.
 *
 * @param {Object} employee - объект сотрудника { id, hired_at, pay_day, ... }
 * @param {Array} scheduleRows - массив строк расписания
 * @param {Object} salaryConfig - конфигурация ЗП (settlementCutoverDate и т.д.)
 * @param {Object} options - опции { todayStr, fallbackEarliestDate, getSalaryPeriods }
 * @returns {Array} массив периодов [{ start, end, ... }]
 */
export function discoverDailyCandidatePeriods(employee, scheduleRows = [], salaryConfig = {}, options = {}) {
  if (!employee) return []
  const todayStr = options.todayStr || null
  const cutover = salaryConfig?.settlementCutoverDate || salaryConfig?.settlement_cutover_date || null
  const getPeriodsFunc = options.getSalaryPeriods || getSalaryPeriods

  // 1. Data discovery boundary: MAX(hired_at, cutover, fallbackEarliestDate)
  // СТРОГО НЕ фильтрует по истории тарифов!
  const empEffectiveStart = getDailyDataDiscoveryStart(employee, salaryConfig, options.fallbackEarliestDate)

  // 2. Отбираем даты смен сотрудника
  const empShiftDates = new Set()
  if (Array.isArray(scheduleRows)) {
    scheduleRows.forEach(s => {
      if (!s) return
      if (s.employee_id && s.employee_id !== employee.id) return
      const dStr = typeof s.work_date === 'string' ? s.work_date.split('T')[0] : ''
      if (dStr && (!empEffectiveStart || dStr >= empEffectiveStart) && (!todayStr || dStr <= todayStr)) {
        let hours = parseFloat(s.hours) || 0
        if (s.approval_status === 'pending' || s.approval_status === 'rejected') {
          hours = 0
        }
        if (hours > 0 || s.started_at) {
          empShiftDates.add(dStr)
        }
      }
    })
  }

  if (todayStr && (!empEffectiveStart || todayStr >= empEffectiveStart)) {
    empShiftDates.add(todayStr)
  }

  // 3. Собираем уникальные месяцы (YYYY-MM) из фактических смен
  const uniqueMonths = new Set()
  empShiftDates.forEach(dStr => {
    uniqueMonths.add(dStr.substring(0, 7))
  })

  // 4. Генерируем натуральные периоды через getSalaryPeriods
  let dailyPeriods = []
  uniqueMonths.forEach(ym => {
    const [y, m] = ym.split('-').map(Number)
    const monthPeriods = getPeriodsFunc(employee.hired_at, y, m, 'daily', employee.pay_day, [], cutover)
    if (Array.isArray(monthPeriods)) {
      dailyPeriods.push(...monthPeriods)
    }
  })

  let targetPeriods = dailyPeriods.filter(p => (!todayStr || p.end <= todayStr) && empShiftDates.has(p.start))
  if (cutover) {
    targetPeriods = targetPeriods.filter(p => p.end >= cutover)
  }

  return targetPeriods.sort((a, b) => a.start.localeCompare(b.start))
}


/**
 * V2 Immutable Payroll Periods Resolution:
 * CURRENT SETTINGS determine FUTURE periods.
 * CURRENT SETTINGS do NOT alter historical immutable periods.
 * Merges existing immutable periods with new period generation without overlaps.
 */
export function resolveEmployeeSalaryPeriods({ employee, salaryConfig, year, month, existingPeriods = [] }) {
  const pad = (n) => String(n).padStart(2, '0')
  const lastDay = new Date(year, month, 0).getDate()
  const monthStart = `${year}-${pad(month)}-01`
  const monthEnd = `${year}-${pad(month)}-${pad(lastDay)}`

  const makeDate = (dateStr) => new Date(dateStr + 'T12:00:00')
  const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  const addDaysToDate = (dateStr, days) => {
    const d = makeDate(dateStr)
    d.setDate(d.getDate() + days)
    return fmtDate(d)
  }

  const effectiveFreq = getEffectivePayoutFrequency(employee, salaryConfig)
  const cutoverDate = salaryConfig?.settlement_cutover_date || salaryConfig?.settlementCutoverDate || null
  const basePeriods = getSalaryPeriods(employee?.hired_at, year, month, effectiveFreq, employee?.pay_day, [], cutoverDate)

  if (!basePeriods || basePeriods.length === 0) return []

  const earliestGridStart = basePeriods[0].start < monthStart ? basePeriods[0].start : monthStart
  const latestGridEnd = basePeriods[basePeriods.length - 1].end > monthEnd ? basePeriods[basePeriods.length - 1].end : monthEnd

  const relevantExisting = (existingPeriods || [])
    .filter(p => {
      const pStart = p.period_start || p.start
      const pEnd = p.period_end || p.end
      const empMatch = !p.employee_id || !employee?.id || p.employee_id === employee.id
      const dateOverlap = pEnd >= earliestGridStart && pStart <= latestGridEnd
      const cutoverMatch = !cutoverDate || pEnd >= cutoverDate
      return empMatch && dateOverlap && cutoverMatch
    })
    .map(p => ({
      id: p.id,
      start: p.period_start || p.start,
      end: p.period_end || p.end,
      displayStart: p.displayStart || p.period_start || p.start,
      frequency: p.frequency || 'weekly',
      status: p.status || 'closed',
      label: p.label || `Период ${(p.period_start || p.start).slice(5)} — ${(p.period_end || p.end).slice(5)}`,
      payoutDate: p.payoutDate || p.payout_date || p.period_end || p.end,
      payoutLabel: p.payoutLabel || p.payout_label || '',
      isImmutable: true
    }))

  if (relevantExisting.length === 0) {
    return basePeriods
  }

  relevantExisting.sort((a, b) => a.start.localeCompare(b.start))
  const latestCoveredEnd = relevantExisting.reduce((maxEnd, p) => p.end > maxEnd ? p.end : maxEnd, relevantExisting[0].end)

  if (latestCoveredEnd >= monthEnd) {
    return relevantExisting
  }

  // Check if latestCoveredEnd falls strictly inside one of the natural basePeriods
  const overlappingNatural = basePeriods.find(bp => bp.start <= latestCoveredEnd && latestCoveredEnd < bp.end)

  let futurePeriods = []
  if (overlappingNatural) {
    const transitionStart = addDaysToDate(latestCoveredEnd, 1)
    const transitionEnd = overlappingNatural.end

    if (transitionStart <= transitionEnd) {
      const transitionPeriod = {
        ...overlappingNatural,
        id: `trans_${transitionStart}_${transitionEnd}`,
        start: transitionStart,
        end: transitionEnd,
        displayStart: transitionStart,
        frequency: effectiveFreq,
        status: 'closed',
        label: `Период ${transitionStart.slice(5)} — ${transitionEnd.slice(5)}`,
        payoutDate: overlappingNatural.payoutDate || transitionEnd,
        payoutLabel: overlappingNatural.payoutLabel || '',
        isTransition: true,
        isImmutable: true
      }

      const subsequentPeriods = basePeriods
        .filter(bp => bp.start > transitionEnd)
        .map(bp => ({ ...bp, frequency: bp.frequency || effectiveFreq }))

      futurePeriods = [transitionPeriod, ...subsequentPeriods]
    } else {
      futurePeriods = basePeriods
        .filter(bp => bp.start > latestCoveredEnd)
        .map(bp => ({ ...bp, frequency: bp.frequency || effectiveFreq }))
    }
  } else {
    // Natural boundary change (no shortened transition period needed)
    futurePeriods = basePeriods
      .filter(bp => bp.start > latestCoveredEnd)
      .map(bp => ({ ...bp, frequency: bp.frequency || effectiveFreq }))
  }

  return [...relevantExisting, ...futurePeriods].sort((a, b) => a.start.localeCompare(b.start))
}

export function getSalaryPeriods(hiredAt, year, month, payoutFrequency = 'bi_monthly', payoutDay = null, existingPeriods = [], cutoverDate = null) {
  let freq = payoutFrequency
  let pDay = payoutDay
  let existP = existingPeriods
  let cutover = cutoverDate

  if (typeof payoutFrequency === 'object' && payoutFrequency !== null) {
    freq = payoutFrequency.payout_frequency || payoutFrequency.payoutFrequency || 'bi_monthly'
    pDay = payoutFrequency.payout_day ?? payoutFrequency.payoutDay ?? payoutDay
    cutover = payoutFrequency.settlement_cutover_date || payoutFrequency.settlementCutoverDate || cutoverDate
  }

  if (existP && existP.length > 0) {
    return resolveEmployeeSalaryPeriods({
      employee: { hired_at: hiredAt, pay_day: pDay },
      salaryConfig: { payout_frequency: freq, settlement_cutover_date: cutover },
      year,
      month,
      existingPeriods: existP
    })
  }
  const pad = (n) => String(n).padStart(2, '0')
  const lastDay = new Date(year, month, 0).getDate()
  const mm = pad(month)

  // Безопасное форматирование без toISOString
  const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

  // Создать дату без UTC-сдвига
  const makeDate = (dateStr) => new Date(dateStr + 'T12:00:00')

  // Дата + N дней
  const addDaysToDate = (dateStr, days) => {
    const d = makeDate(dateStr)
    d.setDate(d.getDate() + days)
    return fmtDate(d)
  }

  // Нормализация целевого дня недели:
  // 0 = Вс, 1 = Пн ... 6 = Сб. Если передан 7 (вс по ГОСТ/ISO) -> 0.
  let targetDay = 1
  if (payoutDay != null) {
    const parsed = parseInt(payoutDay, 10)
    if (!isNaN(parsed)) {
      targetDay = parsed === 7 ? 0 : (parsed % 7)
    }
  }

  // Найти целевой день недели внутри окна [windowStart, windowEnd].
  // Если нет — ближайший целевой день недели после windowEnd.
  const findWeekdayInWindow = (windowStart, windowEnd) => {
    const start = makeDate(windowStart)
    const end = makeDate(windowEnd)

    // Перебираем дни от windowStart до windowEnd
    const current = new Date(start)
    while (current <= end) {
      if (current.getDay() === targetDay) {
        return { date: fmtDate(current), inWindow: true }
      }
      current.setDate(current.getDate() + 1)
    }

    // Целевого дня в окне нет — ищем ближайший после конца окна
    const after = new Date(end)
    after.setDate(after.getDate() + 1)
    while (after.getDay() !== targetDay) {
      after.setDate(after.getDate() + 1)
    }
    return { date: fmtDate(after), inWindow: false }
  }
  const findMondayInWindow = findWeekdayInWindow

  const formatPayoutLabel = (dateStr) => {
    const d = makeDate(dateStr)
    const dayNames = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб']
    return `${d.getDate()} ${d.toLocaleDateString('ru-RU', { month: 'short' })} (${dayNames[d.getDay()]})`
  }

  const hireDate = hiredAt ? hiredAt.split('T')[0] : null
  const hireDay = hireDate ? parseInt(hireDate.split('-')[2], 10) : 1

  // Определяем группу выплаты по дню трудоустройства
  // Группа A: день 1-7 или 16-23
  // Группа B: день 8-15 или 24-31
  const getPaymentGroup = (day) => {
    if (day >= 1 && day <= 7) return 'A'
    if (day >= 8 && day <= 15) return 'B'
    if (day >= 16 && day <= 23) return 'A'
    return 'B' // 24+
  }

  const group = getPaymentGroup(hireDay)

  // Вычисляем окна выплат для каждого периода
  const getPaymentWindow = (periodType) => {
    // periodType: 'first' (1-15) или 'second' (16-end)
    if (periodType === 'first') {
      if (group === 'A') {
        // 15-20 текущего месяца
        return {
          start: `${year}-${mm}-15`,
          end: `${year}-${mm}-20`
        }
      } else {
        // 20-25 текущего месяца
        return {
          start: `${year}-${mm}-20`,
          end: `${year}-${mm}-25`
        }
      }
    } else {
      // Следующий месяц
      const nextMonth = month === 12 ? 1 : month + 1
      const nextYear = month === 12 ? year + 1 : year
      const nmm = pad(nextMonth)

      if (group === 'A') {
        // 1-5 следующего месяца
        return {
          start: `${nextYear}-${nmm}-01`,
          end: `${nextYear}-${nmm}-05`
        }
      } else {
        // 5-10 следующего месяца
        return {
          start: `${nextYear}-${nmm}-05`,
          end: `${nextYear}-${nmm}-10`
        }
      }
    }
  }

  const periods = []
  const noHireDate = !hiredAt

  // === Поддержка разных периодичностей выплат ===

  if (freq === 'weekly') {
    // 1. Еженедельные периоды
    const startOfMonth = new Date(year, month - 1, 1)
    const endOfMonth = new Date(year, month, 0)

    // Находим первый понедельник (может быть в конце прошлого месяца)
    let current = new Date(startOfMonth)
    while (current.getDay() !== 1) { // 1 = Понедельник
      current.setDate(current.getDate() - 1)
    }

    // Генерируем недели
    while (current <= endOfMonth) {
      const pStart = new Date(current)
      const pEnd = new Date(current)
      pEnd.setDate(pEnd.getDate() + 6)

      const startStr = fmtDate(pStart)
      const endStr = fmtDate(pEnd)

      // Cutover isolation: если задан cutover, недели до cutover пропускаем, если месяц >= месяц cutover
      if (cutover) {
        const cutoverMonthPrefix = cutover.slice(0, 7)
        const currentMonthPrefix = `${year}-${mm}`
        if (currentMonthPrefix >= cutoverMonthPrefix && endStr < cutover) {
          current.setDate(current.getDate() + 7)
          continue
        }
      }

      if (!hireDate || hireDate <= endStr) {
        let effectiveStart = (hireDate && hireDate > startStr) ? hireDate : startStr
        if (cutover && cutover > startStr && cutover <= endStr) {
          if (!hireDate || hireDate < cutover) {
            effectiveStart = cutover
          }
        }
        const actualStart = (cutover && cutover > startStr && cutover <= endStr) ? cutover : startStr

        // Окно выплаты: Пн-Вт следующей недели
        const payStart = new Date(pEnd)
        payStart.setDate(payStart.getDate() + 1)
        const payEnd = new Date(pEnd)
        payEnd.setDate(payEnd.getDate() + 2)

        const payout = findMondayInWindow(fmtDate(payStart), fmtDate(payEnd))

        periods.push({
          start: actualStart,
          end: endStr,
          displayStart: effectiveStart,
          label: `Неделя ${pStart.getDate()}.${pStart.getMonth()+1} – ${pEnd.getDate()}.${pEnd.getMonth()+1}`,
          payoutDate: payout.date,
          payoutLabel: formatPayoutLabel(payout.date),
          payoutWindow: `${payStart.getDate()}–${payEnd.getDate()} ${pEnd.getMonth() === 11 ? 'янв' : new Date(pEnd.getFullYear(), pEnd.getMonth() + 1, 1).toLocaleDateString('ru-RU', { month: 'short' })}`,
          payoutInWindow: payout.inWindow,
          group: 'A'
        })
      }

      current.setDate(current.getDate() + 7)
    }
  } else if (freq === 'monthly') {
    // 2. Ежемесячные периоды
    let pStart = `${year}-${mm}-01`
    const pEnd = `${year}-${mm}-${pad(lastDay)}`

    if (cutover && cutover > pStart && cutover <= pEnd) {
      pStart = cutover
    }

    if (!hireDate || hireDate <= pEnd) {
      const nextMonth = month === 12 ? 1 : month + 1
      const nextYear = month === 12 ? year + 1 : year
      const nmm = pad(nextMonth)
      const windowStart = `${nextYear}-${nmm}-01`
      const windowEnd = `${nextYear}-${nmm}-05`
      const payout = findMondayInWindow(windowStart, windowEnd)

      periods.push({
        start: pStart,
        end: pEnd,
        displayStart: (hireDate && hireDate > pStart) ? hireDate : pStart,
        label: `Весь месяц`,
        payoutDate: payout.date,
        payoutLabel: formatPayoutLabel(payout.date),
        payoutWindow: `1–5 ${month === 12 ? 'янв' : new Date(year, month, 1).toLocaleDateString('ru-RU', { month: 'short' })}`,
        payoutInWindow: payout.inWindow,
        group: 'A'
      })
    }
  } else if (freq === 'daily') {
    // 3. Ежедневные периоды: каждый календарный день месяца — отдельный settlement period
    for (let d = 1; d <= lastDay; d++) {
      const dayStr = `${year}-${mm}-${pad(d)}`
      if (cutover && dayStr < cutover) continue
      if (hireDate && hireDate > dayStr) continue

      const dayDate = makeDate(dayStr)
      const dayNames = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб']
      const dayName = dayNames[dayDate.getDay()]
      const monthName = dayDate.toLocaleDateString('ru-RU', { month: 'short' })
      const label = `${d} ${monthName} (${dayName})`

      periods.push({
        start: dayStr,
        end: dayStr,
        displayStart: dayStr,
        label: label,
        payoutDate: dayStr,
        payoutLabel: label,
        payoutWindow: `${d} ${monthName}`,
        payoutInWindow: true,
        frequency: 'daily',
        group: 'A'
      })
    }
  } else {
    // 4. Полумесячные периоды (bi_monthly / bi_weekly)
    let p1Start = `${year}-${mm}-01`
    const p1End = `${year}-${mm}-15`

    if (cutover && cutover > p1Start && cutover <= p1End) {
      p1Start = cutover
    }

    const cutoverMonthPrefix = cutover ? cutover.slice(0, 7) : null
    const currentMonthPrefix = `${year}-${mm}`
    const skipP1 = cutover && currentMonthPrefix >= cutoverMonthPrefix && p1End < cutover

    if (!skipP1) {
      if (!hireDate || hireDate <= p1End) {
        const effectiveStart = (hireDate && hireDate > p1Start) ? hireDate : p1Start
        const effectiveStartDay = parseInt(effectiveStart.split('-')[2], 10)

        const window1 = getPaymentWindow('first')
        const payout1 = findMondayInWindow(window1.start, window1.end)

        periods.push({
          start: p1Start,
          end: p1End,
          displayStart: effectiveStart,
          label: effectiveStartDay > 1 ? `${effectiveStartDay}–15` : `1–15`,
          payoutDate: payout1.date,
          payoutLabel: formatPayoutLabel(payout1.date),
          payoutWindow: `${makeDate(window1.start).getDate()}–${makeDate(window1.end).getDate()}`,
          payoutInWindow: payout1.inWindow,
          group: group,
        })
      }
    }

    let p2Start = `${year}-${mm}-16`
    const p2End = `${year}-${mm}-${pad(lastDay)}`

    if (cutover && cutover > p2Start && cutover <= p2End) {
      p2Start = cutover
    }

    const skipP2 = cutover && currentMonthPrefix >= cutoverMonthPrefix && p2End < cutover

    if (!skipP2) {
      if (!hireDate || hireDate <= p2End) {
        const effectiveStart = (hireDate && hireDate > p2Start) ? hireDate : p2Start
        const effectiveStartDay = parseInt(effectiveStart.split('-')[2], 10)

        const window2 = getPaymentWindow('second')
        const payout2 = findMondayInWindow(window2.start, window2.end)

        periods.push({
          start: p2Start,
          end: p2End,
          displayStart: effectiveStart,
          label: effectiveStartDay > 16 ? `${effectiveStartDay}–${lastDay}` : `16–${lastDay}`,
          payoutDate: payout2.date,
          payoutLabel: formatPayoutLabel(payout2.date),
          payoutWindow: `${makeDate(window2.start).getDate()}–${makeDate(window2.end).getDate()} ${month === 12 ? 'янв' : new Date(year, month, 1).toLocaleDateString('ru-RU', { month: 'short' })}`,
          payoutInWindow: payout2.inWindow,
          group: group,
        })
      }
    }
  }

  if (periods.length === 0) {
    const window2 = getPaymentWindow('second')
    const payout2 = findMondayInWindow(window2.start, window2.end)
    periods.push({
      start: `${year}-${mm}-01`,
      end: `${year}-${mm}-${pad(lastDay)}`,
      displayStart: hireDate || `${year}-${mm}-01`,
      label: 'Весь месяц',
      payoutDate: payout2.date,
      payoutLabel: formatPayoutLabel(payout2.date),
      payoutWindow: '',
      payoutInWindow: payout2.inWindow,
      group: group,
    })
  }

  // Метаданные
  periods._noHireDate = noHireDate
  periods._group = group

  return periods
}

/**
 * Resolves whether a date range represents a natural settlement period or an arbitrary analytics range
 * specifically for the given employee.
 *
 * @param {Object} employee - Employee profile object (role, hired_at, pay_day, employment_type, payout_frequency_override, etc.)
 * @param {string} rangeStart - Start of date range (YYYY-MM-DD)
 * @param {string} rangeEnd - End of date range (YYYY-MM-DD)
 * @param {Object} salaryConfig - Salary configuration of the PVZ (payout_frequency, cutover, etc.)
 * @returns {Object} { mode: 'settlement' | 'analytics', isAnalyticsRange: boolean, isSettlementPeriod: boolean, matchedNaturalPeriod: boolean, naturalPeriod: Object }
 */
export function resolveEmployeeSalaryRangeMode(employee, rangeStart, rangeEnd, salaryConfig = {}) {
  if (!rangeStart || !rangeEnd) {
    return {
      mode: 'analytics',
      isAnalyticsRange: true,
      isSettlementPeriod: false,
      matchedNaturalPeriod: false,
      naturalPeriod: { start: rangeStart || '', end: rangeEnd || '', displayStart: rangeStart || '' }
    }
  }

  const effectiveFreq = getEffectivePayoutFrequency(employee, salaryConfig)
  const cutoverDate = salaryConfig?.settlement_cutover_date || salaryConfig?.settlementCutoverDate || null

  const pStartParts = String(rangeStart).split('-').map(Number)
  const pEndParts = String(rangeEnd).split('-').map(Number)

  const startYear = pStartParts[0] || new Date().getFullYear()
  const startMonth = pStartParts[1] || (new Date().getMonth() + 1)
  const endYear = pEndParts[0] || startYear
  const endMonth = pEndParts[1] || startMonth

  const rawPeriods = []
  let curY = startYear
  let curM = startMonth - 1
  if (curM < 1) { curM = 12; curY -= 1; }

  let targetEndY = endYear
  let targetEndM = endMonth + 1
  if (targetEndM > 12) { targetEndM = 1; targetEndY += 1; }

  while (curY < targetEndY || (curY === targetEndY && curM <= targetEndM)) {
    const list = getSalaryPeriods(employee?.hired_at, curY, curM, effectiveFreq, employee?.pay_day, [], cutoverDate)
    rawPeriods.push(...list)
    curM += 1
    if (curM > 12) { curM = 1; curY += 1; }
  }

  const matchedPeriod = rawPeriods.find(p => p.start === rangeStart && p.end === rangeEnd) || null
  const matchedNaturalPeriod = Boolean(matchedPeriod)

  return {
    mode: matchedNaturalPeriod ? 'settlement' : 'analytics',
    isAnalyticsRange: !matchedNaturalPeriod,
    isSettlementPeriod: matchedNaturalPeriod,
    matchedNaturalPeriod,
    naturalPeriod: matchedPeriod || { start: rangeStart, end: rangeEnd, displayStart: rangeStart }
  }
}

async function calculatePeriodDebt(employeeId, p, pvzId, config, todayStr, preloadedData = null, options = { mode: 'settlement' }) {
  let sal

  if (preloadedData) {
    // Выполняем чисто в памяти
    const periodLogs = preloadedData.logs.filter(l => l.work_date >= p.start && l.work_date <= p.end)
    const periodSchedule = preloadedData.schedule.filter(s => s.work_date >= p.start && s.work_date <= p.end)
    const periodPaymentsForSalaryCalculation = preloadedData.payments.filter(pm => {
      if (pm.employee_id !== employeeId) return false
      if (Array.isArray(pm.allocations) && pm.allocations.length > 0) {
        return pm.allocations.some(a => a.period_start === p.start && a.period_end === p.end)
      }
      // Historical legacy payment without allocations:
      // Include if it matches exactly or has any overlap with period p (so unresolved can be tracked)
      const pStart = pm.period_start || pm.date
      const pEnd = pm.period_end || pm.date
      const cutoverDate = config?.settlement_cutover_date || config?.settlementCutoverDate || null
      if (cutoverDate && p.start >= cutoverDate && pEnd < cutoverDate) {
        return false
      }
      return (pEnd >= p.start && pStart <= p.end)
    })
    const periodDeductions = preloadedData.deductions.filter(d => d.date >= p.start && d.date <= p.end)
    const periodLinkedHelpers = preloadedData.linkedHelpers.filter(lh => lh.work_date >= p.start && lh.work_date <= p.end)

    sal = calculateSalaryFromData(
      preloadedData.profile,
      periodLogs,
      periodSchedule,
      periodPaymentsForSalaryCalculation,
      periodDeductions,
      periodLinkedHelpers,
      config,
      p.start,
      p.end,
      preloadedData.configHistory,
      preloadedData.allPvzSchedule || [],
      todayStr,
      options
    )
  } else {
    // Обычный сетевой путь (fallbacks)
    const configHistory = await getSalaryConfigHistory(pvzId)
    sal = await calculateAutoSalaryWithHistory(employeeId, p.start, p.end, pvzId, configHistory, todayStr, options)
  }

  const isCompleteChunk = p.isCompleteChunk !== false
  const todayHasOpenShift = (preloadedData?.schedule || []).some(s => {
    if (s.employee_id && s.employee_id !== employeeId) return false
    const isAutoShift = !s.hours_source || s.hours_source === 'auto_shift' || s.hours_source === 'system_auto'
    return isAutoShift && s.started_at && !s.ended_at && s.work_date === todayStr
  })
  const hasOpenShift = Boolean(sal.hasOpenShift || sal.isOpenShift || todayHasOpenShift)
  const isReview = Boolean(sal.isReviewRequired)

  // Инвариант: для неполного чанка или открытой смены долг к плановой выплате равен 0
  const isPayable = isCompleteChunk && !hasOpenShift && !isReview && ((sal.periodDebt || 0) > 0)
  const debt = isPayable ? (sal.periodDebt || 0) : 0
  const payableDebt = isPayable ? (sal.periodDebt || 0) : 0
  const accruedDebt = (sal.periodDebt !== undefined && sal.periodDebt !== null) ? sal.periodDebt : (sal.total || 0)

  return {
    period: p,
    salary: sal,
    debt: debt,
    payableDebt: payableDebt,
    accruedDebt: accruedDebt,
    isCompleteChunk: isCompleteChunk,
    isPayable: isPayable,
    shiftsCountInChunk: p.shiftsCountInChunk || null,
    requiredShiftsCount: p.requiredShiftsCount || null,
    hasOpenShift: hasOpenShift,
    isOpenShift: hasOpenShift,
    rateEarned: sal.rateEarned,
    bonusesEarned: sal.bonusesEarned,
    ratePaid: sal.ratePaid,
    bonusesPaid: sal.bonusesPaid,
    rateDebt: isPayable ? sal.rateDebt : 0,
    bonusesDebt: isPayable ? sal.bonusesDebt : 0,
    bonusesPayable: sal.bonusesPayable,
    bonusPayoutDateStr: sal.bonusPayoutDateStr,
    splitEnabled: sal.splitEnabled,
    isHelper: sal.isHelper,
    isReviewRequired: sal.isReviewRequired,
    unresolvedLegacyPayments: sal.unresolvedLegacyPayments,
    hasUnresolvedLegacy: sal.hasUnresolvedLegacy,
    status: sal.payrollHistoryReliable === false ? 'history_unavailable' : (isReview ? 'unresolved' : (!isCompleteChunk ? 'incomplete_chunk' : (hasOpenShift ? 'open_shift' : sal.status))),
    unresolvedDebt: sal.unresolvedDebt,
    confirmedDebt: isPayable ? (sal.confirmedDebt || 0) : 0,
    unresolvedLegacyTotal: sal.unresolvedLegacyTotal,
    unresolvedLegacyNotice: sal.payrollHistoryReliable === false
      ? 'ФОТ недоступен — нет истории тарифов на эту дату'
      : sal.unresolvedLegacyNotice
  }
}

function formatPayoutLabelShort(dateStr) {
  if (!dateStr) return ''
  const makeDate = (dateStr) => new Date(dateStr + 'T12:00:00')
  const d = makeDate(dateStr)
  const dayNames = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб']
  return `${d.getDate()} ${d.toLocaleDateString('ru-RU', { month: 'short' })} (${dayNames[d.getDay()]})`
}

export async function getEmployeeActualSalaryPeriod(employeeId, hiredAt, pvzId, employeeProfile = null, options = { mode: 'settlement' }) {
  // Закрываем зависшие смены ТОЛЬКО в мутирующем режиме (readOnly: true строго исключает мутации)
  if (!options?.readOnly) {
    await autoCloseExpiredShifts(pvzId)
  }

  const today = new Date()
  const config = await getSalaryConfig(pvzId)
  let configHistory = []
  let configHistoryLoadFailed = false
  try {
    const historyRes = await getSalaryConfigHistory(pvzId)
    configHistory = Array.isArray(historyRes) ? historyRes : (historyRes?.data || [])
  } catch (err) {
    configHistoryLoadFailed = true
    configHistory = []
  }
  const todayStr = getDateInTimeZone(today, config?.timezone)

  if (configHistoryLoadFailed) {
    return {
      period: {
        start: todayStr,
        end: todayStr,
        displayStart: todayStr,
        label: 'Период недоступен',
        payoutDate: todayStr,
        payoutLabel: formatPayoutLabelShort(todayStr),
        effectiveFrequency: 'shifts'
      },
      debt: 0,
      accruedDebt: 0,
      isPayable: false,
      isReviewRequired: true,
      financialDataUnavailable: true,
      status: 'data_unavailable',
      notice: 'Не удалось загрузить историю тарифов. Расчёт выплат временно недоступен — повторите загрузку.'
    }
  }

  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth()

  let targetProfile = employeeProfile
  if (!targetProfile) {
    const emps = await apiClient(`/employees?pvzId=${pvzId}`).catch(() => [])
    targetProfile = (emps || []).find(e => e.id === employeeId) || {}
  }

  const effectiveFrequency = getEffectivePayoutFrequency(targetProfile, config)
  const periods = []

  if (effectiveFrequency === 'shifts') {
    const shiftsCount = parseInt(config.payoutShiftsCount) || 10
    const empHireDate = (hiredAt || targetProfile?.hired_at)?.split('T')[0] || null
    const cutoverDate = config?.settlement_cutover_date || config?.settlementCutoverDate || null

    let startForShifts = null
    const knownBoundaries = []
    if (empHireDate) knownBoundaries.push(empHireDate)
    if (cutoverDate) knownBoundaries.push(cutoverDate)

    if (knownBoundaries.length > 0) {
      startForShifts = knownBoundaries.reduce((max, d) => (d > max ? d : max), knownBoundaries[0])
    } else {
      // Никаких угаданных дат (2026-03-01 и т.д.). Ищем фактическую первую активность через эндпоинт
      let discoveryFailure = false
      try {
        const query = new URLSearchParams({ pvzId, employeeId })
        const res = await apiClient(`/schedule/earliest-activity?${query.toString()}`)
        if (res && Object.prototype.hasOwnProperty.call(res, 'earliestDate')) {
          if (res.earliestDate) {
            startForShifts = res.earliestDate
          } else {
            // Success Null: БД подтверждает отсутствие смен в истории
            startForShifts = todayStr
          }
        } else {
          discoveryFailure = true
        }
      } catch (err) {
        discoveryFailure = true
      }

      // Fail-Closed: сетевой сбой СТРОГО НЕ должен превращаться в todayStr с нулевым долгом
      if (discoveryFailure) {
        return {
          period: {
            start: todayStr,
            end: todayStr,
            displayStart: todayStr,
            label: 'Период недоступен',
            payoutDate: todayStr,
            payoutLabel: formatPayoutLabelShort(todayStr),
            effectiveFrequency: 'shifts'
          },
          debt: 0,
          accruedDebt: 0,
          isPayable: false,
          isReviewRequired: true,
          discoveryUnavailable: true,
          status: 'history_unavailable',
          notice: 'Не удалось проверить историю смен. Расчёт выплат временно недоступен — требуется повторная загрузка.'
        }
      }
    }

    // Загрузим смены с фактической границы
    let rawShifts = null
    let shiftsLoadError = false
    try {
      const res = await apiClient(`/schedule?employeeId=${employeeId}&pvzId=${pvzId}&startDate=${startForShifts}&endDate=${todayStr}`)
      rawShifts = Array.isArray(res) ? res : (res?.data || [])
    } catch (err) {
      shiftsLoadError = true
    }

    if (shiftsLoadError) {
      return {
        period: {
          start: todayStr,
          end: todayStr,
          displayStart: todayStr,
          label: 'Период недоступен',
          payoutDate: todayStr,
          payoutLabel: formatPayoutLabelShort(todayStr),
          effectiveFrequency: 'shifts'
        },
        debt: 0,
        accruedDebt: 0,
        isPayable: false,
        isReviewRequired: true,
        financialDataUnavailable: true,
        status: 'data_unavailable',
        notice: 'Не удалось загрузить график смен. Расчёт выплат временно недоступен — повторите загрузку.'
      }
    }

    const sortedShifts = [...(rawShifts || [])]
      .filter(s => {
        if (s.employee_id && s.employee_id !== employeeId) return false
        let hours = parseFloat(s.hours) || 0
        if (s.approval_status === 'pending' || s.approval_status === 'rejected') {
          hours = 0
        }
        return hours > 0 || s.started_at
      })
      .sort((a, b) => a.work_date.localeCompare(b.work_date))

    for (let i = 0; i < sortedShifts.length; i += shiftsCount) {
      const chunk = sortedShifts.slice(i, i + shiftsCount)
      const firstDate = chunk[0].work_date.split('T')[0]
      const lastDate = chunk[chunk.length - 1].work_date.split('T')[0]

      const payoutLabel = formatPayoutLabelShort(lastDate)
      const isCompleteChunk = chunk.length === shiftsCount

      periods.push({
        start: firstDate,
        end: lastDate,
        displayStart: firstDate,
        label: `Смены ${i + 1}–${i + chunk.length}`,
        payoutDate: lastDate,
        payoutLabel: payoutLabel,
        payoutWindow: '',
        payoutInWindow: true,
        group: 'A',
        isCompleteChunk: isCompleteChunk,
        shiftsCountInChunk: chunk.length,
        requiredShiftsCount: shiftsCount
      })
    }

    if (periods.length === 0) {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0]
      periods.push({
        start: startOfMonth,
        end: todayStr,
        displayStart: startOfMonth,
        label: `Смены (нет)`,
        payoutDate: todayStr,
        payoutLabel: formatPayoutLabelShort(todayStr),
        payoutWindow: '',
        payoutInWindow: true,
        group: 'A',
        isCompleteChunk: false,
        shiftsCountInChunk: 0,
        requiredShiftsCount: shiftsCount
      })
    }
  } else {
    const cutoverDate = config?.settlement_cutover_date || config?.settlementCutoverDate || null
    for (let mOffset = -1; mOffset <= 1; mOffset++) {
      const d = new Date(currentYear, currentMonth + mOffset, 1)
      periods.push(...getSalaryPeriods(hiredAt || targetProfile?.hired_at, d.getFullYear(), d.getMonth() + 1, effectiveFrequency, targetProfile?.pay_day, [], cutoverDate))
    }
  }

  if (periods.length === 0) return { period: null, debt: 0 }

  const batchStart = periods[0].start
  const batchEnd = periods[periods.length - 1].end

  // Пакетно загружаем все за 3 месяца!
  const paramsLogs = new URLSearchParams({ employeeId, pvzId, startDate: batchStart, endDate: batchEnd })
  const paramsSchedule = new URLSearchParams({ employeeId, pvzId, startDate: batchStart, endDate: batchEnd })
  const paramsPayments = new URLSearchParams({ employeeId, pvzId, periodStart: batchStart, periodEnd: batchEnd })
  const paramsDeductions = new URLSearchParams({ employeeId, pvzId, startDate: batchStart, endDate: batchEnd })

  const [
    employeesRes,
    logsRes,
    scheduleRes,
    paymentsRes,
    deductionsRes,
    allScheduleRes
  ] = await Promise.all([
    apiClient(`/employees?pvzId=${pvzId}`).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
    apiClient('/work-logs?' + paramsLogs.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
    apiClient('/schedule?' + paramsSchedule.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
    apiClient('/payments?' + paramsPayments.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
    apiClient('/deductions?' + paramsDeductions.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
    apiClient(`/schedule?pvzId=${pvzId}&startDate=${batchStart}&endDate=${batchEnd}`).then(data => ({ data, error: null })).catch(error => ({ data: null, error }))
  ])

  // MISSING_FINANCIAL_DATA_MUST_NOT_BE_INTERPRETED_AS_EMPTY_FINANCIAL_DATA:
  // Если хотя бы один обязательный финансовый датасет упал с ошибкой, обычный payable calculation запрещен!
  const hasFinancialDataError = Boolean(
    logsRes.error ||
    scheduleRes.error ||
    paymentsRes.error ||
    deductionsRes.error ||
    allScheduleRes.error
  )

  if (hasFinancialDataError) {
    const targetPeriod = periods.find(p => todayStr >= p.start && todayStr <= p.end) || periods[periods.length - 1] || {
      start: todayStr,
      end: todayStr,
      displayStart: todayStr,
      label: 'Период недоступен',
      payoutDate: todayStr,
      payoutLabel: formatPayoutLabelShort(todayStr),
      effectiveFrequency
    }
    return {
      period: targetPeriod,
      debt: 0,
      accruedDebt: 0,
      isPayable: false,
      isReviewRequired: true,
      financialDataUnavailable: true,
      status: 'data_unavailable',
      notice: 'Не удалось загрузить все данные для расчёта зарплаты. Выплата временно заблокирована — повторите загрузку.'
    }
  }

  const profileData = (employeesRes.data || []).find(e => e.id === employeeId) || targetProfile || {}
  const profileRes = { data: profileData }
  const linkedHelpersRes = { data: (allScheduleRes.data || []).filter(s => s.linked_to === employeeId) }

  const preloadedData = {
    profile: profileRes.data,
    logs: (logsRes.data || []).filter(x => x.employee_id === employeeId),
    schedule: (scheduleRes.data || []).filter(x => x.employee_id === employeeId),
    payments: (paymentsRes.data || []).filter(x => x.employee_id === employeeId),
    deductions: (deductionsRes.data || []).filter(x => x.employee_id === employeeId),
    linkedHelpers: linkedHelpersRes.data || [],
    allPvzSchedule: allScheduleRes.data || [],
    configHistory
  }

  let lastIncompleteChunkResult = null

  for (const p of periods) {
    const res = await calculatePeriodDebt(employeeId, p, pvzId, config, todayStr, preloadedData, options)

    // Если чанк не полон — сохраняем результат для возврата начисленного заработка, но не отдаем как готовый к плановой выдаче
    if (p.isCompleteChunk === false) {
      lastIncompleteChunkResult = res
      continue
    }

    if (p.payoutDate < todayStr) {
      if (res.debt > 0 && res.isPayable) {
        return res
      }
    } else {
      if (res.debt <= 0 && p.end < todayStr) {
        continue
      }
      return res
    }
  }

  // Если все полные периоды закрыты, а есть неполный чанк — возвращаем его (с debt=0, isPayable=false, accruedDebt=начислено)
  if (lastIncompleteChunkResult) {
    return lastIncompleteChunkResult
  }

  const currentP = periods.find(p => todayStr >= p.start && todayStr <= p.end) || periods[periods.length - 1]
  return await calculatePeriodDebt(employeeId, currentP, pvzId, config, todayStr, preloadedData, options)
}

/**
 * Получить естественный расчётный период для сотрудника на целевую дату (по умолчанию сегодня).
 * Возвращает { start, end, displayStart, label, payoutDate, payoutLabel, effectiveFrequency }
 */
export function getEmployeeCurrentSalaryPeriod(employee, salaryConfig, targetDate = new Date()) {
  const effectiveFreq = getEffectivePayoutFrequency(employee, salaryConfig)
  const pvzTimezone = salaryConfig?.timezone || salaryConfig?.pvz_timezone || null
  const todayStr = getDateInTimeZone(targetDate, pvzTimezone)
  const parts = todayStr.split('-').map(Number)
  const currentYear = parts[0]
  const currentMonth = parts[1] // 1-12

  if (effectiveFreq === 'shifts') {
    const pad = (n) => String(n).padStart(2, '0')
    const lastDay = new Date(currentYear, currentMonth, 0).getDate()
    const start = `${currentYear}-${pad(currentMonth)}-01`
    const end = `${currentYear}-${pad(currentMonth)}-${pad(lastDay)}`
    return {
      start,
      end,
      displayStart: (employee?.hired_at && employee.hired_at.split('T')[0] > start) ? employee.hired_at.split('T')[0] : start,
      label: 'Смены (текущий месяц)',
      payoutDate: todayStr,
      payoutLabel: formatPayoutLabelShort(todayStr),
      effectiveFrequency: 'shifts'
    }
  }

  const cutoverDate = salaryConfig?.settlement_cutover_date || salaryConfig?.settlementCutoverDate || null
  // Загружаем периоды за предыдущий, текущий и следующий месяцы
  const candidatePeriods = []
  for (let mOffset = -1; mOffset <= 1; mOffset++) {
    const d = new Date(currentYear, currentMonth - 1 + mOffset, 1)
    const pList = getSalaryPeriods(employee?.hired_at, d.getFullYear(), d.getMonth() + 1, effectiveFreq, employee?.pay_day, [], cutoverDate)
    candidatePeriods.push(...pList)
  }

  const found = candidatePeriods.find(p => todayStr >= p.start && todayStr <= p.end)
  if (found) {
    return { ...found, effectiveFrequency: effectiveFreq }
  }

  const nearest = candidatePeriods.find(p => p.end >= todayStr) || candidatePeriods[candidatePeriods.length - 1]
  return nearest ? { ...nearest, effectiveFrequency: effectiveFreq } : null
}

/**
 * Получить дату YYYY-MM-DD в часовом поясе выбранного ПВЗ.
 * Использует Intl.DateTimeFormat.formatToParts для гарантированного формата YYYY-MM-DD.
 */
export function getDateInTimeZone(date = new Date(), timeZone) {
  // Минимальный безопасный fallback для legacy ПВЗ без указанного timezone:
  // Сначала системный resolvedOptions устройства (если продукт в разных регионах),
  // и только затем UTC, избегая жесткого принудительного хардкода Europe/Moscow для всех ПВЗ.
  const tz = timeZone || (typeof Intl !== 'undefined' && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : null)

  if (tz) {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour12: false
      })
      const parts = formatter.formatToParts(date instanceof Date ? date : new Date(date))
      let year = '', month = '', day = ''
      for (const p of parts) {
        if (p.type === 'year') year = p.value
        if (p.type === 'month') month = p.value
        if (p.type === 'day') day = p.value
      }
      if (year && month && day) return `${year}-${month}-${day}`
    } catch (e) {
      // timeZone строка некорректна — переходим к fallback
    }
  }

  // Fallback: безопасный UTC формат YYYY-MM-DD
  const d = date instanceof Date ? date : new Date(date)
  const pad2 = (n) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`
}

/**
 * SALARY PAID STATUS V1:
 * Computes settlement-coverage for analytics or settlement periods.
 * Uses natural settlement period semantics and verified payment allocations.
 * Never uses fake FIFO, pro-rata, or synthetic allocations.
 */
export function computePaymentCoverage(params = {}) {
  const profile = params.profile || params.employee || {}
  const periodStart = params.periodStart || params.dateRange?.start || params.start || ''
  const periodEnd = params.periodEnd || params.dateRange?.end || params.end || ''
  const config = { ...(params.config || {}) }
  if (params.cutoverDate && !config.settlementCutoverDate && !config.settlement_cutover_date) {
    config.settlementCutoverDate = params.cutoverDate
  }
  const salaryPayments = params.salaryPayments || params.payments || []
  const payrollPeriods = params.payrollPeriods || []

  let dailyDetails = params.dailyDetails || {}
  if (Array.isArray(dailyDetails)) {
    const map = {}
    dailyDetails.forEach(d => {
      if (d && d.date) {
        map[d.date] = {
          date: d.date,
          hours: d.hours != null ? d.hours : (d.earnings ? 10 : 0),
          total: d.total != null ? d.total : (d.earnings != null ? d.earnings : 0),
          basePay: d.basePay || 0,
          acceptBonus: d.acceptBonus || 0,
          issuedBonus: d.issuedBonus || 0,
          customWorkBonus: d.customWorkBonus || 0,
          bonuses: d.bonuses || 0,
          fines: d.fines || 0,
          helperDeduction: d.helperDeduction || 0,
          wbDeductions: d.wbDeductions || 0,
          ...d
        }
      }
    })
    dailyDetails = map
  }

  const analyticsTotal = params.analyticsTotal != null
    ? params.analyticsTotal
    : (params.totalGross != null ? params.totalGross : (params.total != null ? params.total : 0))

  const isAnalytics = params.isAnalytics !== undefined
    ? params.isAnalytics
    : Boolean(params.dateRange || params.isAnalyticsRange || params.mode === 'analytics' || params.options?.mode === 'analytics')

  const unresolvedLegacyPayments = params.unresolvedLegacyPayments || []
  const ratePaid = params.ratePaid || 0
  const bonusesPaid = params.bonusesPaid || 0
  const isReviewRequired = Boolean(params.isReviewRequired)
  const periodDebt = params.periodDebt

  const round2 = (val) => Math.round((Number(val) || 0) * 100) / 100

  // 1. Settlement Mode: strictly follow settlement debt / review
  if (!isAnalytics) {
    if (isReviewRequired || (unresolvedLegacyPayments && unresolvedLegacyPayments.length > 0)) {
      return {
        paymentStatus: 'review',
        paymentStatusReason: 'settlement_legacy_review',
        paymentPaidAmount: null,
        paymentOutstandingAmount: null,
        paymentAmountsExact: false
      }
    }
    const debt = periodDebt != null ? Math.round(periodDebt) : Math.round(analyticsTotal)
    const paid = Math.round(ratePaid + bonusesPaid)
    if (debt <= 0 && (analyticsTotal > 0 || paid > 0)) {
      return {
        paymentStatus: 'paid',
        paymentStatusReason: 'settlement_fully_paid',
        paymentPaidAmount: round2(analyticsTotal),
        paymentOutstandingAmount: 0,
        paymentAmountsExact: true
      }
    }
    if (paid > 0 && debt > 0) {
      return {
        paymentStatus: 'partial',
        paymentStatusReason: 'settlement_partial_debt',
        paymentPaidAmount: round2(paid),
        paymentOutstandingAmount: round2(debt),
        paymentAmountsExact: true
      }
    }
    return {
      paymentStatus: 'unpaid',
      paymentStatusReason: 'settlement_unpaid',
      paymentPaidAmount: 0,
      paymentOutstandingAmount: round2(debt),
      paymentAmountsExact: true
    }
  }

  // 2. Analytics Mode: determine natural settlement periods
  const effectiveFreq = getEffectivePayoutFrequency(profile, config)
  const cutoverDate = config?.settlement_cutover_date || config?.settlementCutoverDate || null

  // Generate natural periods covering periodStart to periodEnd
  const pStartParts = (periodStart || '').split('-').map(Number)
  const pEndParts = (periodEnd || '').split('-').map(Number)

  const startYear = pStartParts[0] || new Date().getFullYear()
  const startMonth = pStartParts[1] || (new Date().getMonth() + 1)
  const endYear = pEndParts[0] || startYear
  const endMonth = pEndParts[1] || startMonth

  const rawPeriods = []
  let curY = startYear
  let curM = startMonth - 1
  if (curM < 1) { curM = 12; curY -= 1; }

  let targetEndY = endYear
  let targetEndM = endMonth + 1
  if (targetEndM > 12) { targetEndM = 1; targetEndY += 1; }

  while (curY < targetEndY || (curY === targetEndY && curM <= targetEndM)) {
    const list = getSalaryPeriods(profile?.hired_at, curY, curM, effectiveFreq, profile?.pay_day, [], cutoverDate)
    rawPeriods.push(...list)
    curM += 1
    if (curM > 12) { curM = 1; curY += 1; }
  }

  // Deduplicate and sort periods
  const periodMap = new Map()
  rawPeriods.forEach(p => {
    const key = `${p.start}_${p.end}`
    if (!periodMap.has(key)) periodMap.set(key, p)
  })
  const allNaturalPeriods = Array.from(periodMap.values()).sort((a, b) => a.start.localeCompare(b.start))

  // Find natural periods overlapping [periodStart, periodEnd]
  const overlappingPeriods = allNaturalPeriods.filter(p => p.end >= periodStart && p.start <= periodEnd)

  // Determine contributing periods: those that have actual accruals / activity in the range [periodStart, periodEnd]
  const contributingPeriods = []

  for (const np of overlappingPeriods) {
    const overlapStart = np.start > periodStart ? np.start : periodStart
    const overlapEnd = np.end < periodEnd ? np.end : periodEnd

    let hasActivity = false
    let earnedInRangeForPeriod = 0

    const curDate = new Date(overlapStart + 'T12:00:00')
    const endDate = new Date(overlapEnd + 'T12:00:00')

    while (curDate <= endDate) {
      const pad2 = (n) => String(n).padStart(2, '0')
      const dStr = `${curDate.getFullYear()}-${pad2(curDate.getMonth() + 1)}-${pad2(curDate.getDate())}`
      const day = dailyDetails[dStr]
      if (day) {
        const dayActivity = (day.hours > 0) || (day.total !== 0) || (day.basePay !== 0) ||
          (day.acceptBonus !== 0) || (day.issuedBonus !== 0) || (day.customWorkBonus !== 0) ||
          (day.bonuses !== 0) || (day.fines !== 0) || (day.helperDeduction !== 0) || (day.wbDeductions !== 0)
        if (dayActivity) {
          hasActivity = true
          earnedInRangeForPeriod += Number(day.total || 0)
        }
      }
      curDate.setDate(curDate.getDate() + 1)
    }

    if (hasActivity) {
      contributingPeriods.push({
        period: np,
        overlapStart,
        overlapEnd,
        earnedInRange: earnedInRangeForPeriod,
        isFullyContained: periodStart <= np.start && periodEnd >= np.end
      })
    }
  }

  // Edge case: if no contributing periods (e.g. 0 accruals in entire range)
  if (contributingPeriods.length === 0) {
    return {
      paymentStatus: 'none',
      paymentStatusReason: 'no_accruals_in_range',
      paymentPaidAmount: 0,
      paymentOutstandingAmount: 0,
      paymentAmountsExact: true
    }
  }

  // Check for legacy ambiguity in payments affecting contributing periods
  let hasAmbiguousLegacy = false
  // Fail-closed positive allowlist: only confirmed paid payments count towards settlement coverage
  const activePayments = (salaryPayments || []).filter(p => p.status === 'paid')

  for (const cp of contributingPeriods) {
    const np = cp.period
    for (const pm of activePayments) {
      const isAllocated = Array.isArray(pm.allocations) && pm.allocations.length > 0
      if (!isAllocated) {
        const pStart = pm.period_start || pm.date
        const pEnd = pm.period_end || pm.date || pStart
        if (!pStart && !pEnd) {
          hasAmbiguousLegacy = true
          break
        }
        if (cutoverDate && np.start >= cutoverDate && pEnd < cutoverDate) {
          continue
        }
        const isCrossingCutover = Boolean(cutoverDate && pStart < cutoverDate && pEnd >= cutoverDate)
        const overlaps = pEnd >= np.start && pStart <= np.end
        if (overlaps && (np.end <= (cutoverDate || '9999') || pStart !== np.start || pEnd !== np.end || isCrossingCutover)) {
          hasAmbiguousLegacy = true
          break
        }
      }
    }
    if (hasAmbiguousLegacy) break
  }

  if (isReviewRequired || (unresolvedLegacyPayments && unresolvedLegacyPayments.length > 0) || hasAmbiguousLegacy) {
    return {
      paymentStatus: 'review',
      paymentStatusReason: 'legacy_ambiguity',
      paymentPaidAmount: null,
      paymentOutstandingAmount: null,
      paymentAmountsExact: false
    }
  }

  // Evaluate each contributing period's settlement status
  let allFullyPaid = true
  let anyPaidOrPartial = false
  let allUnpaid = true
  let canComputeExact = true
  let totalPaidExact = 0
  let totalOutstandingExact = 0

  for (const cp of contributingPeriods) {
    const np = cp.period

    let allocSum = 0
    let exactLegacySum = 0

    for (const pm of activePayments) {
      if (Array.isArray(pm.allocations) && pm.allocations.length > 0) {
        const matches = pm.allocations.filter(a => a.period_start === np.start && a.period_end === np.end)
        allocSum += matches.reduce((s, a) => s + Number(a.allocated_amount || 0), 0)
      } else {
        const pStart = pm.period_start || pm.date
        const pEnd = pm.period_end || pm.date
        if (pStart === np.start && pEnd === np.end) {
          exactLegacySum += Number(pm.amount || 0)
        }
      }
    }

    const totalAllocated = allocSum + exactLegacySum

    if (cp.isFullyContained) {
      const periodEarned = cp.earnedInRange
      const isFullyPaid = totalAllocated >= periodEarned && periodEarned > 0
      if (isFullyPaid) {
        anyPaidOrPartial = true
        allUnpaid = false
        totalPaidExact += periodEarned
      } else if (totalAllocated > 0) {
        allFullyPaid = false
        anyPaidOrPartial = true
        allUnpaid = false
        totalPaidExact += totalAllocated
        totalOutstandingExact += Math.max(0, periodEarned - totalAllocated)
      } else {
        allFullyPaid = false
        totalOutstandingExact += periodEarned
      }
    } else {
      // Range cuts this natural period!
      // Calculate full natural period earned to verify if the entire natural period was settled
      let fullNaturalPeriodEarned = 0
      const npCurDate = new Date(np.start + 'T12:00:00')
      const npEndDate = new Date(np.end + 'T12:00:00')
      while (npCurDate <= npEndDate) {
        const pad2 = (n) => String(n).padStart(2, '0')
        const dStr = `${npCurDate.getFullYear()}-${pad2(npCurDate.getMonth() + 1)}-${pad2(npCurDate.getDate())}`
        const day = dailyDetails[dStr]
        if (day) {
          fullNaturalPeriodEarned += Number(day.total || 0)
        }
        npCurDate.setDate(npCurDate.getDate() + 1)
      }

      const isPeriodFullySettled = fullNaturalPeriodEarned > 0
        ? totalAllocated >= fullNaturalPeriodEarned
        : totalAllocated >= cp.earnedInRange && totalAllocated > 0

      if (isPeriodFullySettled) {
        anyPaidOrPartial = true
        allUnpaid = false
        totalPaidExact += cp.earnedInRange
      } else if (totalAllocated <= 0) {
        allFullyPaid = false
        totalOutstandingExact += cp.earnedInRange
      } else {
        // Partial allocation for cut period: NO PRO-RATA!
        allFullyPaid = false
        anyPaidOrPartial = true
        allUnpaid = false
        canComputeExact = false
      }
    }
  }

  if (allFullyPaid) {
    return {
      paymentStatus: 'paid',
      paymentStatusReason: 'all_contributing_periods_paid',
      paymentPaidAmount: round2(analyticsTotal),
      paymentOutstandingAmount: 0,
      paymentAmountsExact: true
    }
  }

  if (allUnpaid) {
    return {
      paymentStatus: 'unpaid',
      paymentStatusReason: 'all_contributing_periods_unpaid',
      paymentPaidAmount: 0,
      paymentOutstandingAmount: round2(analyticsTotal),
      paymentAmountsExact: true
    }
  }

  return {
    paymentStatus: 'partial',
    paymentStatusReason: canComputeExact ? 'partial_with_exact_amounts' : 'partial_cut_range_no_prorata',
    paymentPaidAmount: canComputeExact ? round2(totalPaidExact) : null,
    paymentOutstandingAmount: canComputeExact ? round2(totalOutstandingExact) : null,
    paymentAmountsExact: canComputeExact
  }
}

export function calculateSalaryFromData(empProfile, logsRaw, scheduleDataRaw, paymentsRaw, wbDeductionsRaw, linkedHelpersRaw, config, periodStart, periodEnd, configHistory = [], allPvzScheduleRaw = [], customTodayStr = null, customOptions = {}) {
  let options = { mode: 'settlement' }
  let todayStrParam = customTodayStr
  if (customTodayStr && typeof customTodayStr === 'object' && !(customTodayStr instanceof Date)) {
    options = { mode: 'settlement', ...customTodayStr }
    todayStrParam = null
  } else if (customOptions && typeof customOptions === 'object') {
    options = { mode: 'settlement', ...customOptions }
  }

  const normalizeDate = (d) => typeof d === 'string' ? d.split('T')[0] : (d ? new Date(d).toISOString().split('T')[0] : d)

  const logs = (logsRaw || []).map(x => ({ ...x, work_date: normalizeDate(x.work_date) }))
  const scheduleData = (scheduleDataRaw || []).map(x => ({ ...x, work_date: normalizeDate(x.work_date) }))
  const payments = (paymentsRaw || []).map(x => ({ ...x, date: normalizeDate(x.period_start || x.created_at), period_start: normalizeDate(x.period_start), period_end: normalizeDate(x.period_end) }))
  const wbDeductions = (wbDeductionsRaw || []).map(x => ({ ...x, date: normalizeDate(x.date) }))
  const linkedHelpers = (linkedHelpersRaw || []).map(x => ({ ...x, work_date: normalizeDate(x.work_date) }))

  const FULL_SHIFT_HOURS = 12
  const isHelper = empProfile?.role === 'helper'

  const scheduleMap = {}
  const shiftRoleMap = {}
  const shiftTimestamps = {} // Phase 2: храним started_at/ended_at

  const pvzTimezone = config?.timezone || config?.pvz_timezone || null
  const todayStr = todayStrParam || getDateInTimeZone(new Date(), pvzTimezone)

  ;(scheduleData || []).forEach(s => {
    let hours = parseFloat(s.hours) || 0

    // Смены с датой ПОСЛЕ текущего бизнес-дня ПВЗ (будущие) не должны начисляться в зарплату
    if (s.work_date > todayStr) {
      hours = 0
    } else if (s.approval_status === 'pending' || s.approval_status === 'rejected') {
      // Не подтвержденные или отклоненные ручные часы не идут в оплату
      hours = 0
    } else {
      const isAutoShift = !s.hours_source || s.hours_source === 'auto_shift' || s.hours_source === 'system_auto'
      if (isAutoShift && s.started_at && s.ended_at) {
        const start = new Date(s.started_at)
        const end = new Date(s.ended_at)
        let diffMs = end - start

        let breaksMs = 0
        if (s.breaks && Array.isArray(s.breaks) && s.breaks.length > 0) {
          for (const b of s.breaks) {
            if (b.start && b.end) {
              const breakMs = new Date(b.end) - new Date(b.start)
              if (breakMs > 0) breaksMs += breakMs
            }
          }
        }
        const ALLOWED_BREAK_MS = 60 * 60 * 1000 // 60 минут
        const breakMsToDeduct = Math.max(0, breaksMs - ALLOWED_BREAK_MS)
        diffMs -= breakMsToDeduct

        if (diffMs > 0) {
          hours = Math.round((diffMs / (1000 * 60 * 60)) * 2) / 2 // Округляем до 0.5ч
          hours = Math.max(0.5, hours)
        }
      } else if (isAutoShift && s.started_at && !s.ended_at) {
        // Смена начата, но не завершена
        if (s.work_date === todayStr) {
          const start = new Date(s.started_at)
          let diffMs = Date.now() - start.getTime()

          let breaksMs = 0
          if (s.breaks && Array.isArray(s.breaks) && s.breaks.length > 0) {
            for (const b of s.breaks) {
              if (b.start && b.end) {
                const breakMs = new Date(b.end) - new Date(b.start)
                if (breakMs > 0) breaksMs += breakMs
              } else if (b.start && !b.end) {
                // Активный перерыв — вычитаем до текущего момента
                const breakMs = Date.now() - new Date(b.start).getTime()
                if (breakMs > 0) breaksMs += breakMs
              }
            }
          }
          const ALLOWED_BREAK_MS = 60 * 60 * 1000 // 60 минут
          const breakMsToDeduct = Math.max(0, breaksMs - ALLOWED_BREAK_MS)
          diffMs -= breakMsToDeduct

          const liveHours = diffMs > 0 ? (diffMs / (1000 * 60 * 60)) : 0

          // SETTLEMENT_PAYABLE vs ANALYTICS_EARNED_SO_FAR:
          // В режиме settlement открытая незакрытая смена за сегодня НЕ является payable (0 подтвержденных часов к выдаче).
          // В режиме analytics учитываются живые часы для live-отображения.
          if (options.mode === 'settlement') {
            hours = 0
          } else {
            hours = liveHours
          }
          s._isOpenShiftToday = true
          s._liveHours = liveHours
        } else {
          // Если смена брошена в прошлые дни — считаем 0 часов до ручного подтверждения
          hours = 0
          // Пометка: смена считается «брошенной» (не закрытой)
          if (!s._abandonedShift) s._abandonedShift = true
        }
      }

      // Плановые записи (is_planned = true) не считаем в зарплату, если нет фактической работы (started_at)
      if (s.is_planned === true && !s.started_at) {
        hours = 0
      }
    }

    scheduleMap[s.work_date] = hours
    shiftRoleMap[s.work_date] = s.shift_role || 'employee'
    shiftTimestamps[s.work_date] = { started_at: s.started_at, ended_at: s.ended_at, is_planned: s.is_planned }
  })

  const totalFines = (payments || [])
    .filter(p => p.type === 'fine' && p.status !== 'rejected')
    .reduce((sum, p) => sum + parseFloat(p.amount), 0)
  const totalBonuses = (payments || [])
    .filter(p => p.type === 'bonus' && p.status !== 'rejected')
    .reduce((sum, p) => sum + parseFloat(p.amount), 0)

  const totalWbDeductions = (wbDeductions || [])
    .filter(d => d.status !== 'rejected')
    .reduce((sum, d) => sum + parseFloat(d.amount), 0)

  // Группируем логи по датам для точечного расчета бонусов
  const logsByDate = {}
  ;(logs || []).forEach(l => {
    if (!logsByDate[l.work_date]) {
      logsByDate[l.work_date] = []
    }
    logsByDate[l.work_date].push(l)
  })

  // Собираем все дни, где была либо смена (schedule), либо операции (work_logs)
  const workDays = new Set()

  // Из schedule: записи с фактически отработанными часами > 0 или с фактическим началом (только не в будущем)
  ;(scheduleData || []).forEach(s => {
    if (s.work_date > todayStr) return
    const hours = scheduleMap[s.work_date] || 0
    if (hours > 0 || s.started_at) {
      workDays.add(s.work_date)
    }
  })

  // Добавляем дни с операциями ТОЛЬКО если у сотрудника была смена в этот день (только не в будущем)
  Object.keys(logsByDate).forEach(date => {
    if (date > todayStr) return
    // Проверяем, есть ли запись в расписании для этой даты
    const hasShift = (scheduleData || []).some(s => s.work_date === date && (s.started_at || (scheduleMap[date] || 0) > 0))
    if (hasShift) {
      workDays.add(date)
    }
  })

  // Считаем "отработанными днями" только те, где была реальная смена с фактически отработанными часами > 0
  const daysWorked = Array.from(workDays).filter(date => {
    return (scheduleMap[date] || 0) > 0
  }).length

  let hasUnreliableHistoryDay = false
  let firstUnreliableReason = null
  let earliestReliableDateFound = null

  if (options.strictHistoricalCoverage && periodStart) {
    const rangeRes = resolveSalaryConfigForDate(periodStart, config, configHistory, options)
    if (!rangeRes.reliable) {
      hasUnreliableHistoryDay = true
      firstUnreliableReason = rangeRes.reason
    }
    if (rangeRes.earliestReliableDate) {
      earliestReliableDateFound = rangeRes.earliestReliableDate
    }
  }

  // Получение актуального конфига на конкретную дату
  const getConfigResolutionForDate = (dateStr) => {
    const res = resolveSalaryConfigForDate(dateStr, config, configHistory, options)
    if (options.strictHistoricalCoverage && !res.reliable) {
      hasUnreliableHistoryDay = true
      if (!firstUnreliableReason) firstUnreliableReason = res.reason
    }
    if (res.earliestReliableDate && !earliestReliableDateFound) {
      earliestReliableDateFound = res.earliestReliableDate
    }
    return res
  }

  // Для обратной совместимости локальных вызовов
  const getConfigForDate = (dateStr) => {
    return getConfigResolutionForDate(dateStr).config
  }

  // Расчёт базовой оплаты и бонусов за товары по дням
  let totalHours = 0
  let basePay = 0
  let acceptBonus = 0
  let issuedBonus = 0
  let returnBonus = 0
  let totalAccepted = 0
  let totalAcceptedLate = 0
  let totalIssued = 0
  let totalReturned = 0

  const dailyDetails = {} // Track daily earnings
  let customWorkBonus = 0
  const customWorkDetails = []

  workDays.forEach(date => {
    const hours = scheduleMap[date] !== undefined ? scheduleMap[date] : 0
    totalHours += hours

    const resolution = getConfigResolutionForDate(date)
    const isDayReliable = !options.strictHistoricalCoverage || resolution.reliable
    const dateConfig = resolution.config

    // Роль сотрудника на КОНКРЕТНЫЙ день (по умолчанию берется из профиля)
    const dateRole = shiftRoleMap[date] || (isHelper ? 'helper' : 'employee')

    const dateLogs = logsByDate[date] || []
    const rawAccepted = dateLogs
      .filter(l => l.type === 'acceptance')
      .reduce((sum, l) => sum + (l.count || 0), 0)
    const rawAcceptedLate = dateLogs
      .filter(l => l.type === 'acceptance')
      .reduce((sum, l) => sum + (l.count_late || 0), 0)
    const rawIssued = dateLogs
      .filter(l => l.type === 'issued')
      .reduce((sum, l) => sum + (l.count || 0), 0)
    const rawReturned = dateLogs
      .filter(l => l.type === 'returns')
      .reduce((sum, l) => sum + (l.count || 0), 0)

    let opsAccepted = rawAccepted
    let opsAcceptedLate = rawAcceptedLate
    let opsIssued = rawIssued
    let opsReturned = rawReturned

    if (dateConfig && dateConfig.bonusCalculationMethod === 'collective_pool_split') {
      // Находим все смены сотрудников на этом ПВЗ за данный день
      const shiftsOnDate = (allPvzScheduleRaw || []).filter(s => {
        const sDate = typeof s.work_date === 'string' ? s.work_date.split('T')[0] : new Date(s.work_date).toISOString().split('T')[0]
        if (sDate !== date) return false

        let sHours = parseFloat(s.hours) || 0
        if (s.approval_status === 'pending' || s.approval_status === 'rejected') {
          sHours = 0
        }
        return sHours > 0 || s.started_at
      })

      const shiftCount = shiftsOnDate.length || 1
      opsAccepted = rawAccepted / shiftCount
      opsAcceptedLate = rawAcceptedLate / shiftCount
      opsIssued = rawIssued / shiftCount
      opsReturned = rawReturned / shiftCount
    }

    totalAccepted += opsAccepted
    totalAcceptedLate += opsAcceptedLate
    totalIssued += opsIssued
    totalReturned += opsReturned

    const hasHelper = !isHelper && (linkedHelpers || []).some(lh => lh.work_date === date && (parseFloat(lh.hours) || 0) > 0)
    const dayEarnings = calculateDayEarnings(hours, dateRole, dateConfig, {
      acceptance: opsAccepted,
      issued: opsIssued,
      acceptanceLate: opsAcceptedLate,
      returned: opsReturned
    }, hasHelper)

    if (isDayReliable) {
      basePay += dayEarnings.basePay
      acceptBonus += dayEarnings.acceptBonus
      issuedBonus += dayEarnings.issuedBonus
      returnBonus += (dayEarnings.returnBonus || 0)
    }

    dailyDetails[date] = {
      date: date,
      hours: hours,
      basePay: isDayReliable ? dayEarnings.basePay : 0,
      acceptBonus: isDayReliable ? dayEarnings.acceptBonus : 0,
      issuedBonus: isDayReliable ? dayEarnings.issuedBonus : 0,
      returnBonus: isDayReliable ? (dayEarnings.returnBonus || 0) : 0,
      opsAccepted: opsAccepted,
      opsAcceptedLate: opsAcceptedLate,
      opsIssued: opsIssued,
      opsReturned: opsReturned,
      hasHelper: hasHelper,
      customWorkBonus: 0,
      fines: 0,
      bonuses: 0,
      helperDeduction: 0,
      wbDeductions: 0,
      total: isDayReliable ? dayEarnings.total : 0,
      payrollReliable: resolution.reliable,
      payrollUnavailableReason: resolution.reliable ? null : resolution.reason,
      // Пометка для незакрытых смен из прошлых дней
      abandonedShift: (scheduleData || []).some(s => s.work_date === date && s._abandonedShift),
      isOpenShift: (scheduleData || []).some(s => s.work_date === date && s._isOpenShiftToday),
      hasOpenShift: (scheduleData || []).some(s => s.work_date === date && s._isOpenShiftToday)
    }

    // Кастомные доплаты посуточно
    if (isDayReliable && !isHelper && dateConfig && dateConfig.customRules && dateConfig.customRules.length > 0) {
      dateConfig.customRules.forEach(rule => {
        if (rule.type === 'fixed') {
          if (hours > 0) {
            const amount = rule.amount
            customWorkBonus += amount

            let detail = customWorkDetails.find(d => d.name === rule.name && d.type === 'fixed')
            if (!detail) {
              detail = { name: rule.name, type: 'fixed', value: 0, amount: 0 }
              customWorkDetails.push(detail)
            }
            detail.value += 1
            detail.amount += amount

            dailyDetails[date].customWorkBonus += amount
            dailyDetails[date].total += amount
          }
        } else {
          const ruleLogs = dateLogs.filter(l => l.type === `custom:${rule.id}`)
          const val = ruleLogs.reduce((s, r) => s + (r.count || 0), 0)
          if (val > 0) {
            let amount = 0
            if (rule.type === 'per_item') {
              amount = val * rule.amount
            } else if (rule.type === 'percent') {
              amount = val * (rule.amount / 100)
            }

            if (amount > 0) {
              customWorkBonus += amount

              let detail = customWorkDetails.find(d => d.name === rule.name && d.type === rule.type)
              if (!detail) {
                detail = { name: rule.name, type: rule.type, value: 0, amount: 0 }
                customWorkDetails.push(detail)
              }
              detail.value += val
              detail.amount += amount

              dailyDetails[date].customWorkBonus += amount
              dailyDetails[date].total += amount
            }
          }
        }
      })
    }
  })

  basePay = Math.round(basePay * 100) / 100
  acceptBonus = Math.round(acceptBonus * 100) / 100
  issuedBonus = Math.round(issuedBonus * 100) / 100
  returnBonus = Math.round(returnBonus * 100) / 100

  // Удержание за помощника посуточно
  let helperDeduction = 0
  if (!isHelper) {
    if (linkedHelpers && linkedHelpers.length > 0) {
      linkedHelpers.forEach(h => {
        if ((parseFloat(h.hours) || 0) <= 0) return

        const resolution = getConfigResolutionForDate(h.work_date)
        const isHelperDayReliable = !options.strictHistoricalCoverage || resolution.reliable
        const dateConf = resolution.config
        if (!dateConf) return
        const rate = dateConf.helperHourlyRate ?? 100
        const pct = dateConf.helperDeductionPercent ?? 50
        const d = Math.round(((parseFloat(h.hours) || 0) * rate) * (pct / 100) * 100) / 100

        if (isHelperDayReliable) {
          helperDeduction += d
        }

        if (h.work_date) {
          if (!dailyDetails[h.work_date]) {
            dailyDetails[h.work_date] = { date: h.work_date, hours: 0, basePay: 0, acceptBonus: 0, issuedBonus: 0, customWorkBonus: 0, fines: 0, bonuses: 0, helperDeduction: 0, wbDeductions: 0, total: 0 }
          }
          if (isHelperDayReliable) {
            dailyDetails[h.work_date].helperDeduction += d
            dailyDetails[h.work_date].total -= d
          }
        }
      })
      helperDeduction = Math.round(helperDeduction * 100) / 100
    }
  }

  // Fines and Bonuses
  ;(payments || []).forEach(p => {
     if (p.status !== 'rejected') {
         const date = p.date || p.period_start || periodStart
         if (date) {
             const resolution = getConfigResolutionForDate(date)
             const isDayReliable = !options.strictHistoricalCoverage || resolution.reliable
             if (!dailyDetails[date]) {
               dailyDetails[date] = {
                 date,
                 hours: 0,
                 basePay: 0,
                 acceptBonus: 0,
                 issuedBonus: 0,
                 customWorkBonus: 0,
                 fines: 0,
                 bonuses: 0,
                 helperDeduction: 0,
                 wbDeductions: 0,
                 total: 0,
                 payrollReliable: resolution.reliable,
                 payrollUnavailableReason: resolution.reliable ? null : resolution.reason
               }
             }
             if (p.type === 'fine') {
                 dailyDetails[date].fines += parseFloat(p.amount)
                 if (isDayReliable) {
                   dailyDetails[date].total -= parseFloat(p.amount)
                 }
             } else if (p.type === 'bonus') {
                 dailyDetails[date].bonuses += parseFloat(p.amount)
                 if (isDayReliable) {
                   dailyDetails[date].total += parseFloat(p.amount)
                 }
             }
         }
     }
  })

  // WB deductions
  ;(wbDeductions || []).forEach(d => {
      if (d.status !== 'rejected') {
          const date = d.date || periodStart
          if (date) {
            const resolution = getConfigResolutionForDate(date)
            const isDayReliable = !options.strictHistoricalCoverage || resolution.reliable
            if (!dailyDetails[date]) {
              dailyDetails[date] = {
                date: date,
                hours: 0,
                basePay: 0,
                acceptBonus: 0,
                issuedBonus: 0,
                customWorkBonus: 0,
                fines: 0,
                bonuses: 0,
                helperDeduction: 0,
                wbDeductions: 0,
                total: 0,
                payrollReliable: resolution.reliable,
                payrollUnavailableReason: resolution.reliable ? null : resolution.reason
              }
            }
            dailyDetails[date].wbDeductions += parseFloat(d.amount)
            if (isDayReliable) {
              dailyDetails[date].total -= parseFloat(d.amount)
            }
          }
      }
  })

  // Strict mode guarantee: unreliable days must never carry a non-zero total
  if (options.strictHistoricalCoverage) {
    Object.keys(dailyDetails).forEach(d => {
      if (dailyDetails[d].payrollReliable === false) {
        dailyDetails[d].total = 0
      }
    })
  }

  // === Расчет автоматических бонусов за объем операций (volume_bonus) ===
  let volumeBonusesAmount = 0
  const cfgRules = config?.custom_rules || config?.customRules || []
  if (!isHelper && cfgRules.length > 0 && (!options.strictHistoricalCoverage || !hasUnreliableHistoryDay)) {
    cfgRules.forEach(rule => {
      if (rule.type === 'volume_bonus' && rule.active !== false) {
        let currentVal = 0
        if (rule.metric === 'accepted') currentVal = totalAccepted
        else if (rule.metric === 'issued') currentVal = totalIssued
        else if (rule.metric === 'returns') currentVal = totalReturned
        else if (rule.metric === 'hours') {
          currentVal = Object.values(dailyDetails).reduce((sum, d) => sum + (d.hours || 0), 0)
        }

        if (currentVal >= parseFloat(rule.threshold || 0)) {
          const amt = parseFloat(rule.amount || 0)
          volumeBonusesAmount += amt

          let detail = customWorkDetails.find(d => d.name === rule.name && d.type === 'volume_bonus')
          if (!detail) {
            detail = { name: rule.name, type: 'volume_bonus', value: currentVal, amount: 0 }
            customWorkDetails.push(detail)
          }
          detail.amount += amt
        }
      }
    })
  }

  const isStrictUnreliable = Boolean(options.strictHistoricalCoverage && hasUnreliableHistoryDay)
  const finalCustomWorkBonus = isStrictUnreliable ? 0 : (customWorkBonus + volumeBonusesAmount)
  const subtotal = isStrictUnreliable ? 0 : (basePay + acceptBonus + issuedBonus + returnBonus + finalCustomWorkBonus)
  const total = isStrictUnreliable ? 0 : (subtotal + totalBonuses - totalFines - helperDeduction - totalWbDeductions)

  // Расчет остатка долга за период
  let rateEarned = isStrictUnreliable ? 0 : (basePay - helperDeduction)
  let bonusesEarned = isStrictUnreliable ? 0 : (acceptBonus + issuedBonus + returnBonus + finalCustomWorkBonus + totalBonuses - totalFines - totalWbDeductions)

  if (bonusesEarned < 0) {
    rateEarned = Math.max(0, rateEarned + bonusesEarned)
    bonusesEarned = 0
  }

  const salaryPayments = (paymentsRaw || []).filter(p => p.type === 'salary' && p.status !== 'rejected' && p.status !== 'cancelled')

  let ratePaid = 0
  let bonusesPaid = 0
  const unresolvedLegacyPayments = []
  const cutoverDate = config?.settlement_cutover_date || config?.settlementCutoverDate || null

  const isExplicitAnalytics = options.mode === 'analytics' || options.isAnalytics === true
  const isAnalytics = isExplicitAnalytics

  salaryPayments.forEach(pm => {
    let effectiveAmount = 0

    if (Array.isArray(pm.allocations) && pm.allocations.length > 0) {
      // NEW SETTLEMENT MODEL: strictly use period_start / period_end matching allocations
      const matchingAllocs = pm.allocations.filter(a =>
        a.period_start === periodStart && a.period_end === periodEnd
      )
      effectiveAmount = matchingAllocs.reduce((sum, a) => sum + Number(a.allocated_amount || 0), 0)
    } else {
      // LEGACY SETTLEMENT MODEL (DO NOT GUESS / DO NOT FALSE ATTRIBUTE):
      const pStart = pm.period_start || pm.date
      const pEnd = pm.period_end || pm.date

      // SAFE CUTOVER ISOLATION:
      // If a persistent cutover boundary is defined:
      // A payment whose covered period ended strictly BEFORE cutoverDate belongs to the Legacy Era.
      // - In settlement mode (periodStart >= cutoverDate): it must NOT block or contaminate post-cutover settlement.
      // - In analytics mode (isAnalytics): it must NOT be flagged as unresolvedLegacyPayments or cause false review.
      if (cutoverDate && (periodStart >= cutoverDate || isAnalytics) && pEnd < cutoverDate) {
        return
      }

      const isCrossingCutover = Boolean(cutoverDate && pStart < cutoverDate && pEnd >= cutoverDate)

      if (pStart === periodStart && pEnd === periodEnd && !isCrossingCutover) {
        // EXACT LEGACY MATCH: 100% matches this natural period (and does not cross cutover era boundary)
        effectiveAmount = Number(pm.amount || 0)
      } else {
        // MULTI-PERIOD / AMBIGUOUS LEGACY (including boundary-crossing legacy payments):
        // DO NOT attribute 100% to first period. DO NOT invent fake allocations.
        // Track as unresolved legacy payment with neutral notice.
        const overlaps = pEnd >= periodStart && pStart <= periodEnd
        if (overlaps) {
          unresolvedLegacyPayments.push({
            id: pm.id,
            amount: Number(pm.amount || 0),
            period_start: pStart,
            period_end: pEnd,
            comment: pm.comment
          })
        }
      }
    }

    if (effectiveAmount <= 0) return

    if (pm.reason_code === 'rate') {
      ratePaid += effectiveAmount
    } else if (pm.reason_code === 'bonus') {
      bonusesPaid += effectiveAmount
    } else {
      const remainingRateDebt = Math.max(rateEarned - ratePaid, 0)
      if (effectiveAmount <= remainingRateDebt) {
        ratePaid += effectiveAmount
      } else {
        ratePaid += remainingRateDebt
        bonusesPaid += (effectiveAmount - remainingRateDebt)
      }
    }
  })

  const rateDebtRaw = Math.max(rateEarned - ratePaid, 0)
  const bonusesDebtRaw = Math.max(bonusesEarned - bonusesPaid, 0)

  // Проверяем доступность бонусов (pay_bonuses_monthly)
  let bonusesPayable = true
  let bonusPayoutDateStr = ''
  const splitEnabled = config.payBonusesMonthly || config.pay_bonuses_monthly || false

  if (splitEnabled && periodStart) {
    const bPayoutDay = config.bonusPayoutDay || config.bonus_payout_day || 5
    const cfgRules = config.custom_rules || config.customRules || []
    const bPayoutModeRule = cfgRules.find(r => r.id === 'system:bonus_payout_month_mode')
    const bPayoutMode = bPayoutModeRule ? bPayoutModeRule.value : (config.bonusPayoutMonthMode || 'next')

    const pParts = periodStart.split('-')
    const pY = parseInt(pParts[0], 10)
    const pM = parseInt(pParts[1], 10)
    let payoutY = pY, payoutM = pM

    if (bPayoutMode !== 'current') {
      payoutM += 1
      if (payoutM > 12) { payoutM = 1; payoutY += 1 }
    }

    const pad2 = (n) => String(n).padStart(2, '0')
    bonusPayoutDateStr = `${payoutY}-${pad2(payoutM)}-${pad2(bPayoutDay)}`

    const todayCheck = todayStr
    bonusesPayable = todayCheck >= bonusPayoutDateStr
  }

  const rawDebt = rateDebtRaw + (bonusesPayable ? bonusesDebtRaw : 0)
  const hasUnresolved = unresolvedLegacyPayments.length > 0

  const hasOpenShift = (scheduleData || []).some(s => {
    const isAutoShift = !s.hours_source || s.hours_source === 'auto_shift' || s.hours_source === 'system_auto'
    return isAutoShift && s.started_at && !s.ended_at && s.work_date === todayStr
  })

  // CRITICAL V2 SAFETY:
  // Для AMBIGUOUS legacy выплат система НЕ показывает ложный долг к выплате,
  // исключает сумму из обычных подтверждённых выплат и устанавливает REVIEW REQUIRED.
  // В режиме аналитики (isAnalytics) показ отработанных начислений не блокируется сверкой.
  // При strictHistoricalCoverage отсутствие надежной истории тарифов также блокирует ложный долг (ISSUE-011).
  const hasHistoryGap = Boolean(options.strictHistoricalCoverage && hasUnreliableHistoryDay)
  const isReviewRequired = isAnalytics ? false : (hasUnresolved || hasHistoryGap)
  const periodDebt = isAnalytics ? null : (isReviewRequired ? 0 : rawDebt)
  const rateDebt = isAnalytics ? null : (isReviewRequired ? 0 : rateDebtRaw)
  const bonusesDebt = isReviewRequired ? 0 : bonusesDebtRaw
  const paid = ratePaid + bonusesPaid

  // SETTLEMENT ELIGIBILITY: открытая незакрытая смена за сегодня не является payable к немедленной выдаче
  const isPayable = isAnalytics ? true : (!hasOpenShift && !isReviewRequired && ((periodDebt || 0) > 0))
  const payableDebt = isPayable ? (periodDebt || 0) : 0

  const paymentCoverage = computePaymentCoverage({
    profile: empProfile,
    periodStart,
    periodEnd,
    dailyDetails,
    salaryPayments,
    rawDebt,
    rateDebtRaw,
    bonusesDebtRaw,
    analyticsTotal: total,
    isAnalytics,
    unresolvedLegacyPayments,
    ratePaid,
    bonusesPaid,
    isReviewRequired,
    periodDebt
  })

  return {
    daysWorked,
    totalHours,
    fullShiftHours: FULL_SHIFT_HOURS,
    totalAccepted,
    totalAcceptedLate,
    totalIssued,
    totalReturned,
    basePay,
    acceptBonus,
    issuedBonus,
    returnBonus,
    customWorkBonus: finalCustomWorkBonus,
    customWorkDetails,
    totalFines,
    totalBonuses,
    helperDeduction,
    totalWbDeductions,
    subtotal,
    total,
    totalGross: total,
    paid,
    periodDebt,
    rateDebt,
    bonusesDebt,
    rateEarned,
    bonusesEarned,
    ratePaid,
    bonusesPaid,
    bonusesPayable,
    bonusPayoutDateStr,
    splitEnabled,
    isHelper,
    config,
    dailyDetails,
    isOpenShift: Boolean(hasOpenShift),
    hasOpenShift: Boolean(hasOpenShift),
    isPayable: isPayable,
    payableDebt: payableDebt,
    accruedDebt: isAnalytics ? total : rawDebt,
    unresolvedLegacyPayments: isAnalytics ? [] : unresolvedLegacyPayments,
    hasUnresolvedLegacy: isAnalytics ? false : hasUnresolved,
    isReviewRequired: isReviewRequired,
    status: isReviewRequired ? (hasHistoryGap ? 'history_unavailable' : 'unresolved') : (isAnalytics ? 'analytics' : (periodDebt <= 0 ? 'closed' : 'open')),
    unresolvedDebt: isReviewRequired ? rawDebt : 0,
    confirmedDebt: isReviewRequired || isAnalytics ? 0 : (periodDebt || 0),
    unresolvedLegacyTotal: isAnalytics ? 0 : unresolvedLegacyPayments.reduce((s, p) => s + (p.amount || 0), 0),
    unresolvedLegacyNotice: isReviewRequired
      ? (hasHistoryGap ? 'ФОТ недоступен — нет истории тарифов на эту дату' : 'Требуется сверка исторических выплат')
      : null,
    isAnalytics: Boolean(isAnalytics),
    payrollHistoryReliable: !hasUnreliableHistoryDay,
    payrollUnavailableReason: hasUnreliableHistoryDay ? firstUnreliableReason : null,
    earliestReliableDate: earliestReliableDateFound || null,
    paymentStatus: paymentCoverage.paymentStatus,
    paymentStatusReason: paymentCoverage.paymentStatusReason,
    paymentPaidAmount: paymentCoverage.paymentPaidAmount,
    paymentOutstandingAmount: paymentCoverage.paymentOutstandingAmount,
    paymentAmountsExact: paymentCoverage.paymentAmountsExact
  }
}

export async function calculateAutoSalary(employeeId, periodStart, periodEnd, options = { mode: 'settlement' }) {
  const pvzId = getActivePvzId()
  const configHistory = await getSalaryConfigHistory(pvzId)
  return calculateAutoSalaryWithHistory(employeeId, periodStart, periodEnd, pvzId, configHistory, null, options)
}

export async function calculateAutoSalaryWithHistory(employeeId, periodStart, periodEnd, pvzId, configHistory = [], customTodayStr = null, customOptions = { mode: 'settlement' }) {
  const config = await getSalaryConfig(pvzId)
  const FULL_SHIFT_HOURS = 12

  if (!isSupabaseConfigured) {
    return { daysWorked: 0, totalHours: 0, fullShiftHours: FULL_SHIFT_HOURS, totalAccepted: 0, totalIssued: 0, totalReturned: 0, basePay: 0, acceptBonus: 0, issuedBonus: 0, returnBonus: 0, totalFines: 0, totalBonuses: 0, helperDeduction: 0, total: 0, config }
  }

  const effectiveStart = periodStart

  // Загружаем ВСЕ данные параллельно
  const paramsLogs = new URLSearchParams({ employeeId, pvzId, startDate: effectiveStart, endDate: periodEnd })
  const paramsSchedule = new URLSearchParams({ employeeId, pvzId, startDate: effectiveStart, endDate: periodEnd })
  const paramsPayments = new URLSearchParams({ employeeId, pvzId, periodStart: effectiveStart, periodEnd: periodEnd })
  const paramsDeductions = new URLSearchParams({ employeeId, pvzId, startDate: effectiveStart, endDate: periodEnd })

  const [
    employeesRes,
    logsRes,
    scheduleRes,
    paymentsRes,
    deductionsRes,
    allScheduleRes
  ] = await Promise.all([
    apiClient(`/employees?pvzId=${pvzId}`).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
    apiClient('/work-logs?' + paramsLogs.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
    apiClient('/schedule?' + paramsSchedule.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
    apiClient('/payments?' + paramsPayments.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
    apiClient('/deductions?' + paramsDeductions.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
    apiClient(`/schedule?pvzId=${pvzId}&startDate=${effectiveStart}&endDate=${periodEnd}`).then(data => ({ data, error: null })).catch(error => ({ data: null, error }))
  ])

  if (logsRes.error) throw logsRes.error
  if (scheduleRes.error) throw scheduleRes.error
  if (paymentsRes.error) throw paymentsRes.error
  if (deductionsRes.error) throw deductionsRes.error
  if (allScheduleRes.error) throw allScheduleRes.error

  const profile = (employeesRes.data || []).find(e => e.id === employeeId) || {}
  const linkedHelpers = (allScheduleRes.data || []).filter(s => s.linked_to === employeeId)

  const empLogs = (logsRes.data || []).filter(l => l.employee_id === employeeId)
  const empSchedule = (scheduleRes.data || []).filter(s => s.employee_id === employeeId)
  const empPayments = (paymentsRes.data || []).filter(p => p.employee_id === employeeId)
  const empDeductions = (deductionsRes.data || []).filter(d => d.employee_id === employeeId)

  return calculateSalaryFromData(
    profile,
    empLogs,
    empSchedule,
    empPayments,
    empDeductions,
    linkedHelpers,
    config,
    periodStart,
    periodEnd,
    configHistory,
    allScheduleRes.data || [],
    customTodayStr,
    customOptions
  )
}

export async function calculateTeamSalaries(pvzId, periodStart, periodEnd, employees, configHistoryOrOptions = [], maybeOptions = {}) {
  let configHistory = []
  let options = { mode: 'settlement' }
  if (Array.isArray(configHistoryOrOptions)) {
    configHistory = configHistoryOrOptions
    if (maybeOptions && typeof maybeOptions === 'object' && Object.keys(maybeOptions).length > 0) {
      options = maybeOptions
    }
  } else if (configHistoryOrOptions && typeof configHistoryOrOptions === 'object') {
    options = configHistoryOrOptions
  }

  const config = await getSalaryConfig(pvzId)
  const historyToUse = (configHistory && configHistory.length > 0)
    ? configHistory
    : await getSalaryConfigHistory(pvzId)

  if (!isSupabaseConfigured) {
    return employees.map(emp => ({
      employeeId: emp.id,
      employeeName: emp.name,
      daysWorked: 0,
      totalHours: 0,
      basePay: 0,
      acceptBonus: 0,
      issuedBonus: 0,
      total: 0,
      dailyDetails: {}
    }))
  }

  // Закрываем зависшие смены (только если не включен readOnly режим)
  if (!options.readOnly) {
    await autoCloseExpiredShifts(pvzId)
  }

  // Загружаем все данные для ПВЗ за период в 4 параллельных запроса
  const paramsLogs = new URLSearchParams({ pvzId, startDate: periodStart, endDate: periodEnd });
  const paramsSchedule = new URLSearchParams({ pvzId, startDate: periodStart, endDate: periodEnd });
  const paramsPayments = new URLSearchParams({ pvzId, periodStart, periodEnd });
  const paramsDeductions = new URLSearchParams({ pvzId, startDate: periodStart, endDate: periodEnd });

  const [
    logsRes,
    scheduleRes,
    paymentsRes,
    deductionsRes
  ] = await Promise.all([
    apiClient('/work-logs?' + paramsLogs.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
    apiClient('/schedule?' + paramsSchedule.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
    apiClient('/payments?' + paramsPayments.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
    apiClient('/deductions?' + paramsDeductions.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error }))
  ])

  if (logsRes.error) throw logsRes.error
  if (scheduleRes.error) throw scheduleRes.error
  if (paymentsRes.error) throw paymentsRes.error
  if (deductionsRes.error) throw deductionsRes.error

  const allLogs = logsRes.data || []
  const allSchedule = scheduleRes.data || []
  const allPayments = paymentsRes.data || []
  const allDeductions = deductionsRes.data || []

  // Вычисляем зарплату для каждого сотрудника в памяти
  const isAutoMode = options.mode === 'auto' || options.perEmployee === true

  return employees.map(emp => {
    const empLogs = allLogs.filter(l => l.employee_id === emp.id)
    const empSchedule = allSchedule.filter(s => s.employee_id === emp.id)
    const empPayments = allPayments.filter(p => p.employee_id === emp.id)
    const empDeductions = allDeductions.filter(d => d.employee_id === emp.id)
    const empLinkedHelpers = allSchedule.filter(s => s.linked_to === emp.id)

    const empModeInfo = isAutoMode
      ? resolveEmployeeSalaryRangeMode(emp, periodStart, periodEnd, config)
      : null

    const empOptions = empModeInfo
      ? {
          ...options,
          mode: empModeInfo.mode,
          isAnalytics: empModeInfo.isAnalyticsRange,
          isAnalyticsRange: empModeInfo.isAnalyticsRange,
          isSettlementPeriod: empModeInfo.isSettlementPeriod
        }
      : options

    const sal = calculateSalaryFromData(
      {
        id: emp.id,
        role: emp.role,
        hired_at: emp.hired_at,
        pay_day: emp.pay_day,
        employment_type: emp.employment_type,
        payout_frequency_override: emp.payout_frequency_override
      },
      empLogs,
      empSchedule,
      empPayments,
      empDeductions,
      empLinkedHelpers,
      config,
      periodStart,
      periodEnd,
      historyToUse,
      allSchedule,
      null, // customTodayStr
      empOptions
    )

    return {
      employeeId: emp.id,
      employeeName: emp.name,
      ...sal,
      ...(empModeInfo ? {
        calculationMode: empModeInfo.mode,
        isSettlementPeriod: empModeInfo.isSettlementPeriod,
        isAnalyticsRange: empModeInfo.isAnalyticsRange,
        matchedNaturalPeriod: empModeInfo.matchedNaturalPeriod,
        naturalPeriod: empModeInfo.naturalPeriod
      } : {})
    }
  })
}

export async function checkPeriodPaid(employeeId, dateStr, pvzId) {
  if (!isSupabaseConfigured || !employeeId || !dateStr) {
    return { isPaid: false }
  }

  try {
    const parts = dateStr.split('-')
    const year = parts[0]
    const month = parts[1]
    const day = parseInt(parts[2], 10)

    const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate()

    let periodStart, periodEnd, nextPeriodStart
    if (day <= 15) {
      periodStart = `${year}-${month}-01`
      periodEnd = `${year}-${month}-15`
      nextPeriodStart = `${year}-${month}-16`
    } else {
      periodStart = `${year}-${month}-16`
      periodEnd = `${year}-${month}-${String(lastDay).padStart(2, '0')}`
      // Следующий период — 1-е число следующего месяца
      const nextMonth = parseInt(month) === 12 ? 1 : parseInt(month) + 1
      const nextYear = parseInt(month) === 12 ? parseInt(year) + 1 : parseInt(year)
      nextPeriodStart = `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`
    }

    // Считаем начислено за период
    const salary = await calculateAutoSalary(employeeId, periodStart, periodEnd)

    // Считаем выплачено за период
    const salaryPayments = await getPayments({
      periodStart,
      periodEnd,
      pvzId: pvzId || getActivePvzId(),
      type: 'salary',
      employeeId,
    })
    const paid = (salaryPayments || [])
      .filter(p => p.status !== 'cancelled' && p.status !== 'rejected')
      .reduce((sum, p) => sum + Number(p.amount), 0)

    const isPaid = paid >= salary.total && salary.total > 0

    return {
      isPaid,
      earned: salary.total,
      paid,
      periodStart,
      periodEnd,
      periodLabel: `${periodStart.split('-').reverse().join('.')} — ${periodEnd.split('-').reverse().join('.')}`,
      nextPeriodStart,
    }
  } catch (err) {
    console.error('checkPeriodPaid error:', err)
    return { isPaid: false }
  }
}

export function calculateDayEarnings(hours, role, config, ops = { acceptance: 0, issued: 0, acceptanceLate: 0, returned: 0 }, hasHelper = false) {
  if (!config) return { basePay: 0, acceptBonus: 0, issuedBonus: 0, returnBonus: 0, total: 0 }

  const isHelper = role === 'helper'
  const FULL_SHIFT_HOURS = 12

  let basePay = 0
  let acceptBonus = 0
  let issuedBonus = 0
  let returnBonus = 0

  if (isHelper) {
    basePay = hours * (config.helperHourlyRate || 0)
    acceptBonus = (ops.acceptance || 0) * (config.helperOwnBonusPerAccepted || 0)
    issuedBonus = (ops.issued || 0) * (config.helperOwnBonusPerIssued || 0)
    returnBonus = (ops.returned || 0) * (config.helperOwnBonusPerReturned || 0)
  } else {
    if (config.hourlyRate && parseFloat(config.hourlyRate) > 0) {
      basePay = hours * parseFloat(config.hourlyRate)
    } else {
      const ratio = hours / FULL_SHIFT_HOURS
      basePay = (config.dailyRate || 0) * ratio
    }

    let bonusPerAccepted = config.bonusPerAccepted || 0
    let bonusPerIssued = config.bonusPerIssued || 0
    let bonusPerReturned = config.bonusPerReturned || 0

    if (hasHelper) {
      if (config.helperBonusPerAccepted !== undefined && config.helperBonusPerAccepted !== null && config.helperBonusPerAccepted !== '') {
        bonusPerAccepted = parseFloat(config.helperBonusPerAccepted)
      }
      if (config.helperBonusPerIssued !== undefined && config.helperBonusPerIssued !== null && config.helperBonusPerIssued !== '') {
        bonusPerIssued = parseFloat(config.helperBonusPerIssued)
      }
      if (config.helperBonusPerReturned !== undefined && config.helperBonusPerReturned !== null && config.helperBonusPerReturned !== '') {
        bonusPerReturned = parseFloat(config.helperBonusPerReturned)
      }
    }

    acceptBonus = (ops.acceptance || 0) * bonusPerAccepted
    issuedBonus = (ops.issued || 0) * bonusPerIssued
    returnBonus = (ops.returned || 0) * bonusPerReturned
  }

  basePay = Math.round(basePay * 100) / 100
  acceptBonus = Math.round(acceptBonus * 100) / 100
  issuedBonus = Math.round(issuedBonus * 100) / 100
  returnBonus = Math.round(returnBonus * 100) / 100

  return {
    basePay,
    acceptBonus,
    issuedBonus,
    returnBonus,
    total: Math.round(basePay + acceptBonus + issuedBonus + returnBonus)
  }
}

export async function calculateTeamSalary(teamIds, start, end) {
  if (!teamIds || teamIds.length === 0) return {}

  const pvzId = getActivePvzId()
  if (!pvzId) return {}

  let profiles = await apiClient('/employees?pvzId=' + pvzId)
  profiles = profiles.filter(p => teamIds.includes(p.id))

  if (!profiles) return {}

  const mappedEmps = profiles.map(p => ({
    id: p.id,
    name: p.full_name,
    role: p.role,
    hired_at: p.hired_at
  }))

  const teamSalaries = await calculateTeamSalaries(pvzId, start, end, mappedEmps, [], { mode: 'analytics' })

  const results = {}
  teamSalaries.forEach(sal => {
    results[sal.employeeId] = sal
  })

  return results
}

/**
 * Предикат фактически отработанной квалифицированной смены.
 * Единственный источник истины для учета смен в ежедневных отчетах и расчете зарплат.
 */
export function isQualifyingShift(s) {
  if (!s) return false
  if (s.started_at !== null) return true
  if (s.is_planned === true) return false
  const h = s.hours != null ? parseFloat(s.hours) : 0
  if (h > 0 && s.approval_status !== 'pending' && s.approval_status !== 'rejected') {
    return true
  }
  return false
}

/**
 * Канонический чистый хелпер расчета ФОТ дня (используется единообразно в /daily-report и /reports).
 * Строго гарантирует REPORTS_DAY_FOT === DAILY_REPORT_DAY_FOT без дублирования формулы.
 */
export function calculateDayPayroll({
  dayDate,
  employees = [],
  schedules = [],
  workLogs = [],
  payments = [],
  deductions = [],
  salaryConfig = null,
  salaryConfigHistory = [],
  pvzTimezone = 'Europe/Moscow',
  actualWorkedEmployeeIds = null,
  todayStr = null
}) {
  if (!salaryConfig || !dayDate) {
    return {
      totalDailyPayroll: null,
      isAvailable: false,
      payrollHistoryReliable: false,
      payrollUnavailableReason: 'no_config',
      earliestReliableDate: null,
      employeeEarnings: {}
    }
  }

  const normalizedSalaryConfig = normalizeSalaryConfigData(salaryConfig, pvzTimezone)
  const normalizedSalaryConfigHistory = (salaryConfigHistory || []).map((item) => ({
    ...item,
    config: item.config ? normalizeSalaryConfigData(item.config, pvzTimezone) : item.config
  }))

  const daySchedules = (schedules || []).filter((s) => {
    const d = typeof s.work_date === 'string' ? s.work_date.split('T')[0] : (s.work_date || s.date)
    return d === dayDate
  })
  const dayLogs = (workLogs || []).filter((l) => {
    const d = typeof l.work_date === 'string' ? l.work_date.split('T')[0] : (l.work_date || l.date)
    return d === dayDate
  })
  const dayPayments = (payments || []).filter((p) => {
    const d = typeof p.date === 'string' ? p.date.split('T')[0] : (p.period_start || p.date)
    return d === dayDate
  })
  const dayDeductions = (deductions || []).filter((d) => {
    const dt = typeof d.date === 'string' ? d.date.split('T')[0] : d.date
    return dt === dayDate
  })

  const qualifyingShifts = daySchedules.filter(isQualifyingShift)
  const qualifyingEmpIdSet = new Set(
    actualWorkedEmployeeIds ||
    qualifyingShifts.map((s) => s.employee_id).filter(Boolean)
  )
  const activeEmployees = (employees || []).filter((emp) => qualifyingEmpIdSet.has(emp.id))

  const employeeEarnings = {}
  let totalPayroll = 0
  let allReliable = true
  let firstUnavailableReason = null
  let earliestReliableDate = null

  activeEmployees.forEach((emp) => {
    const empLogs = dayLogs.filter((l) => l.employee_id === emp.id)
    const empSchedule = daySchedules.filter((s) => s.employee_id === emp.id)
    const empPayments = dayPayments.filter((p) => p.employee_id === emp.id)
    const empDeductions = dayDeductions.filter((d) => d.employee_id === emp.id)
    const empLinkedHelpers = daySchedules.filter((s) => s.linked_to === emp.id)

    const sal = calculateSalaryFromData(
      {
        id: emp.id,
        role: emp.role,
        hired_at: emp.hired_at,
        pay_day: emp.pay_day,
        employment_type: emp.employment_type,
        payout_frequency_override: emp.payout_frequency_override
      },
      empLogs,
      empSchedule,
      empPayments,
      empDeductions,
      empLinkedHelpers,
      normalizedSalaryConfig,
      dayDate,
      dayDate,
      normalizedSalaryConfigHistory,
      daySchedules,
      todayStr,
      { mode: 'analytics', readOnly: true, strictHistoricalCoverage: true }
    )

    if (sal.payrollHistoryReliable === false) {
      allReliable = false
      if (!firstUnavailableReason) firstUnavailableReason = sal.payrollUnavailableReason
    }
    if (sal.earliestReliableDate && !earliestReliableDate) {
      earliestReliableDate = sal.earliestReliableDate
    }

    employeeEarnings[emp.id] = {
      total: sal.total || 0,
      basePay: sal.basePay || 0,
      acceptBonus: sal.acceptBonus || 0,
      issuedBonus: sal.issuedBonus || 0,
      returnBonus: sal.returnBonus || 0,
      customWorkBonus: sal.customWorkBonus || 0,
      totalBonuses: sal.totalBonuses || 0,
      totalFines: sal.totalFines || 0,
      helperDeduction: sal.helperDeduction || 0,
      totalWbDeductions: sal.totalWbDeductions || 0,
      payrollReliable: sal.payrollHistoryReliable !== false,
      payrollUnavailableReason: sal.payrollUnavailableReason || null,
      fineDetails: empPayments
        .filter((p) => p.type === 'fine' && p.status !== 'rejected')
        .map((f) => ({
          id: f.id,
          amount: Number(f.amount || 0),
          comment: f.comment
        })),
      deductionDetails: empDeductions
        .filter((d) => d.status !== 'rejected')
        .map((d) => ({
          id: d.id,
          amount: Number(d.amount || 0),
          type: d.type,
          comment: d.comment || d.reason
        }))
    }

    totalPayroll += sal.total || 0
  })

  if (activeEmployees.length === 0) {
    const dateRes = resolveSalaryConfigForDate(dayDate, normalizedSalaryConfig, normalizedSalaryConfigHistory, { strictHistoricalCoverage: true })
    if (dateRes && dateRes.reliable === false) {
      allReliable = false
      firstUnavailableReason = dateRes.reason
      earliestReliableDate = dateRes.earliestReliableDate
    }
  }

  return {
    totalDailyPayroll: allReliable ? Math.round(totalPayroll * 100) / 100 : null,
    dayTotal: allReliable ? Math.round(totalPayroll * 100) / 100 : null,
    isAvailable: allReliable,
    payrollHistoryReliable: allReliable,
    payrollUnavailableReason: allReliable ? null : firstUnavailableReason,
    earliestReliableDate,
    employeeEarnings
  }
}

/**
 * Чистый in-memory резолвер актуального расчётного периода сотрудника (Blocker 1 & Blocker 2 fix).
 * Использует ту же каноническую логику, что и getEmployeeActualSalaryPeriod:
 * 1. Проверяет старые завершенные периоды (bi_monthly, weekly, monthly, daily, shifts).
 * 2. Если есть старый неоплаченный период — возвращает его (не теряет overdue debt).
 * 3. Если старый период полностью оплачен — пропускает его (bypass paid chunk/period).
 * 4. Для смен (shifts) — нарезает смены на чанки по N, неполный чанк помечает isCompleteChunk: false, amount: 0.
 * 5. Блокирует выплату при открытой смене (hasOpenShift: true).
 * 6. Для помощников (helper) сохраняет специальный недельный расчет по payoutDay.
 * 7. При сетевом сбое (error в sharedData или configHistoryLoadFailed) — возвращает fail-closed (data_unavailable).
 *
 * НЕ ДЕЛАЕТ СЕТЕВЫХ ЗАПРОСОВ (работает строго от preloaded sharedData).
 */
export async function resolveEmployeeActualSalaryPeriodFromData(employee, pvzId, config, configHistory, sharedData, options = {}) {
  const today = options.today || new Date()
  const todayStr = options.todayStr || getDateInTimeZone(today, config?.timezone)
  const effectiveFrequency = getEffectivePayoutFrequency(employee, config)
  const isHelper = employee.role === 'helper'

  // Fail-closed при ошибке загрузки данных
  if (sharedData?.error || options.configHistoryLoadFailed) {
    return {
      period: {
        start: todayStr,
        end: todayStr,
        displayStart: todayStr,
        label: 'Период недоступен',
        payoutDate: todayStr,
        payoutLabel: formatPayoutLabelShort(todayStr),
        effectiveFrequency
      },
      debt: 0,
      accruedDebt: 0,
      isPayable: false,
      isCompleteChunk: false,
      hasOpenShift: false,
      isReviewRequired: true,
      financialDataUnavailable: true,
      status: 'data_unavailable',
      notice: options.configHistoryLoadFailed
        ? 'Не удалось загрузить историю тарифов. Расчёт выплат временно недоступен — повторите загрузку.'
        : 'Не удалось загрузить данные для расчёта зарплаты. Выплата временно заблокирована — повторите загрузку.'
    }
  }

  // Специальная семантика Дашборда для помощников (helper)
  if (isHelper) {
    const dayOfWeek = today.getDay() || 7
    const monday = new Date(today)
    monday.setDate(today.getDate() - (dayOfWeek - 1))
    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)
    const startDate = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`
    const endDate = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`
    const pday = config?.payoutDay || 1
    const targetPayout = new Date(monday)
    targetPayout.setDate(monday.getDate() + (pday - 1))
    if (targetPayout < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
      targetPayout.setDate(targetPayout.getDate() + 7)
    }
    const payoutDateStr = `${targetPayout.getFullYear()}-${String(targetPayout.getMonth() + 1).padStart(2, '0')}-${String(targetPayout.getDate()).padStart(2, '0')}`
    const payoutDate = targetPayout.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }) + ` (${targetPayout.toLocaleDateString('ru-RU', { weekday: 'short' })})`
    const periodLabel = `${monday.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} – ${sunday.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}`

    const helperPeriod = {
      start: startDate,
      end: endDate,
      displayStart: startDate,
      label: periodLabel,
      payoutDate: payoutDateStr,
      payoutLabel: payoutDate,
      effectiveFrequency: 'weekly'
    }

    const preloadedData = {
      profile: employee,
      logs: (sharedData.logs || []).filter(x => x.employee_id === employee.id),
      schedule: (sharedData.schedule || []).filter(x => x.employee_id === employee.id),
      payments: (sharedData.payments || []).filter(x => x.employee_id === employee.id),
      deductions: (sharedData.deductions || []).filter(x => x.employee_id === employee.id),
      linkedHelpers: [],
      allPvzSchedule: sharedData.allPvzSchedule || [],
      configHistory: configHistory || []
    }

    const res = await calculatePeriodDebt(employee.id, helperPeriod, pvzId, config, todayStr, preloadedData, { mode: 'settlement', readOnly: true })
    return res
  }

  // Для обычных сотрудников
  const periods = []
  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth()

  if (effectiveFrequency === 'shifts') {
    const shiftsCount = parseInt(config?.payoutShiftsCount) || 10
    const empHireDate = employee?.hired_at?.split('T')[0] || null
    const cutoverDate = config?.settlement_cutover_date || config?.settlementCutoverDate || null

    let startForShifts = null
    const knownBoundaries = []
    if (empHireDate) knownBoundaries.push(empHireDate)
    if (cutoverDate) knownBoundaries.push(cutoverDate)
    if (employee?.earliest_activity_date) knownBoundaries.push(employee.earliest_activity_date)
    if (Array.isArray(employee?.activity_months) && employee.activity_months.length > 0) {
      const sortedM = [...employee.activity_months].sort()
      knownBoundaries.push(`${sortedM[0]}-01`)
    }

    if (knownBoundaries.length > 0) {
      startForShifts = knownBoundaries.reduce((max, d) => (d > max ? d : max), knownBoundaries[0])
    } else {
      const lookupStatus = sharedData?.earliestActivityLookupStatus || (sharedData?.earliestActivityDate ? 'success' : (sharedData?.error ? 'error' : 'not_needed'))
      if (lookupStatus === 'error') {
        return {
          period: {
            start: todayStr,
            end: todayStr,
            displayStart: todayStr,
            label: 'Период недоступен',
            payoutDate: todayStr,
            payoutLabel: formatPayoutLabelShort(todayStr),
            effectiveFrequency: 'shifts'
          },
          debt: 0,
          accruedDebt: 0,
          isPayable: false,
          isCompleteChunk: false,
          hasOpenShift: false,
          isReviewRequired: true,
          discoveryUnavailable: true,
          financialDataUnavailable: true,
          status: 'data_unavailable',
          notice: 'Не удалось проверить историю смен. Расчёт выплат временно недоступен — требуется повторная загрузка.'
        }
      } else if (lookupStatus === 'success' && sharedData?.earliestActivityDate) {
        startForShifts = sharedData.earliestActivityDate
      } else if (lookupStatus === 'empty') {
        startForShifts = todayStr
      } else if (sharedData?.earliestActivityDate) {
        startForShifts = sharedData.earliestActivityDate
      } else {
        startForShifts = todayStr
      }
    }

    const rawShifts = (sharedData.schedule || []).filter(s => {
      if (s.employee_id && s.employee_id !== employee.id) return false
      let hours = parseFloat(s.hours) || 0
      if (s.approval_status === 'pending' || s.approval_status === 'rejected') {
        hours = 0
      }
      return (hours > 0 || s.started_at) && s.work_date >= startForShifts && s.work_date <= todayStr
    })

    const sortedShifts = [...rawShifts].sort((a, b) => a.work_date.localeCompare(b.work_date))

    for (let i = 0; i < sortedShifts.length; i += shiftsCount) {
      const chunk = sortedShifts.slice(i, i + shiftsCount)
      const firstDate = chunk[0].work_date.split('T')[0]
      const lastDate = chunk[chunk.length - 1].work_date.split('T')[0]
      const payoutLabel = formatPayoutLabelShort(lastDate)
      const isCompleteChunk = chunk.length === shiftsCount

      periods.push({
        start: firstDate,
        end: lastDate,
        displayStart: firstDate,
        label: `Смены ${i + 1}–${i + chunk.length}`,
        payoutDate: lastDate,
        payoutLabel: payoutLabel,
        payoutWindow: '',
        payoutInWindow: true,
        group: 'A',
        isCompleteChunk,
        shiftsCountInChunk: chunk.length,
        requiredShiftsCount: shiftsCount
      })
    }

    if (periods.length === 0) {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0]
      periods.push({
        start: startOfMonth,
        end: todayStr,
        displayStart: startOfMonth,
        label: 'Смены (нет)',
        payoutDate: todayStr,
        payoutLabel: formatPayoutLabelShort(todayStr),
        payoutWindow: '',
        payoutInWindow: true,
        group: 'A',
        isCompleteChunk: false,
        shiftsCountInChunk: 0,
        requiredShiftsCount: shiftsCount
      })
    }
  } else {
    const cutoverDate = config?.settlement_cutover_date || config?.settlementCutoverDate || null
    if (employee?.is_legacy && Array.isArray(employee?.activity_months) && employee.activity_months.length > 0) {
      // O(activity ranges): нарезаем периоды только для месяцев с фактической активностью
      const sortedMonths = [...new Set(employee.activity_months)].sort()
      for (const ym of sortedMonths) {
        const parts = ym.split('-').map(Number)
        if (parts[0] && parts[1]) {
          periods.push(...getSalaryPeriods(employee?.hired_at, parts[0], parts[1], effectiveFrequency, employee?.pay_day, [], cutoverDate))
        }
      }
    } else {
      for (let mOffset = -1; mOffset <= 1; mOffset++) {
        const d = new Date(currentYear, currentMonth + mOffset, 1)
        periods.push(...getSalaryPeriods(employee?.hired_at, d.getFullYear(), d.getMonth() + 1, effectiveFrequency, employee?.pay_day, [], cutoverDate))
      }
    }
  }

  if (periods.length === 0) {
    return {
      period: null,
      debt: 0,
      accruedDebt: 0,
      isPayable: false,
      isCompleteChunk: false,
      hasOpenShift: false
    }
  }

  const preloadedData = {
    profile: employee,
    logs: (sharedData.logs || []).filter(x => x.employee_id === employee.id),
    schedule: (sharedData.schedule || []).filter(x => x.employee_id === employee.id),
    payments: (sharedData.payments || []).filter(x => x.employee_id === employee.id),
    deductions: (sharedData.deductions || []).filter(x => x.employee_id === employee.id),
    linkedHelpers: (sharedData.allPvzSchedule || []).filter(s => s.linked_to === employee.id),
    allPvzSchedule: sharedData.allPvzSchedule || [],
    configHistory: configHistory || []
  }

  let lastIncompleteChunkResult = null

  for (const p of periods) {
    const res = await calculatePeriodDebt(employee.id, p, pvzId, config, todayStr, preloadedData, { mode: 'settlement', readOnly: true })

    if (p.isCompleteChunk === false) {
      lastIncompleteChunkResult = res
      continue
    }

    if (p.payoutDate < todayStr) {
      if (res.debt > 0 && res.isPayable) {
        return res
      }
    } else {
      if (res.debt <= 0 && p.end < todayStr) {
        continue
      }
      return res
    }
  }

  if (lastIncompleteChunkResult) {
    return lastIncompleteChunkResult
  }

  const currentP = periods.find(p => todayStr >= p.start && todayStr <= p.end) || periods[periods.length - 1]
  return await calculatePeriodDebt(employee.id, currentP, pvzId, config, todayStr, preloadedData, { mode: 'settlement', readOnly: true })
}

/**
 * Чистый планировщик фактических диапазонов дат для пакетной загрузки зарплатных данных.
 * Устраняет скачивание монолитных интервалов по возрасту самого старого сотрудника:
 * O(unique factual ranges) вместо O(calendar age).
 *
 * @param {Array} targetEmployees
 * @param {Object} salaryConfig
 * @param {Date|string} today
 * @param {string|null} earliestActivityDate
 * @returns {Array<{ start: string, end: string }>}
 */
export function planFactualDateRanges(targetEmployees = [], salaryConfig = {}, today = new Date(), earliestActivityDate = null) {
  if (!targetEmployees || targetEmployees.length === 0) {
    return []
  }

  const todayStr = getDateInTimeZone(today, salaryConfig?.timezone)
  const todayParts = todayStr.split('-').map(Number)
  const currentYear = todayParts[0]
  const currentMonth = todayParts[1] // 1-12

  let prevMonth = currentMonth - 1
  let prevYear = currentYear
  if (prevMonth < 1) {
    prevMonth = 12
    prevYear -= 1
  }
  const startOfPrevMonth = `${prevYear}-${String(prevMonth).padStart(2, '0')}-01`

  let nextMonth = currentMonth + 1
  let nextYear = currentYear
  if (nextMonth > 12) {
    nextMonth = 1
    nextYear += 1
  }
  const lastDayOfNextMonth = new Date(nextYear, nextMonth, 0).getDate()
  const endOfNextMonth = `${nextYear}-${String(nextMonth).padStart(2, '0')}-${String(lastDayOfNextMonth).padStart(2, '0')}`

  const cutoverDate = salaryConfig?.settlement_cutover_date || salaryConfig?.settlementCutoverDate || null

  const rawRanges = []

  for (const emp of targetEmployees) {
    if (emp.is_legacy) {
      if (Array.isArray(emp.activity_months) && emp.activity_months.length > 0) {
        for (const ym of emp.activity_months) {
          if (typeof ym === 'string' && /^\d{4}-\d{2}$/.test(ym)) {
            const parts = ym.split('-').map(Number)
            const lastDay = new Date(parts[0], parts[1], 0).getDate()
            let mStart = `${ym}-01`
            const mEnd = `${ym}-${String(lastDay).padStart(2, '0')}`
            if (cutoverDate && cutoverDate > mEnd) {
              // Месяц целиком до cutover
              continue
            }
            if (cutoverDate && cutoverDate > mStart) {
              mStart = cutoverDate
            }
            if (mStart <= mEnd) {
              rawRanges.push({ start: mStart, end: mEnd })
            }
          }
        }
      } else if (emp.earliest_activity_date) {
        let sDate = emp.earliest_activity_date
        if (cutoverDate && cutoverDate > sDate) sDate = cutoverDate
        rawRanges.push({ start: sDate, end: endOfNextMonth })
      } else if (emp.hired_at) {
        let sDate = emp.hired_at.split('T')[0]
        if (cutoverDate && cutoverDate > sDate) sDate = cutoverDate
        rawRanges.push({ start: sDate, end: endOfNextMonth })
      } else {
        const sDate = (cutoverDate && cutoverDate > startOfPrevMonth) ? cutoverDate : startOfPrevMonth
        rawRanges.push({ start: sDate, end: endOfNextMonth })
      }
    } else {
      const freq = getEffectivePayoutFrequency(emp, salaryConfig)
      const empHireDate = emp.hired_at ? emp.hired_at.split('T')[0] : null

      if (freq === 'shifts') {
        const known = []
        if (empHireDate) known.push(empHireDate)
        if (cutoverDate) known.push(cutoverDate)

        let reqStart = null
        if (known.length > 0) {
          reqStart = known.reduce((max, d) => (d > max ? d : max), known[0])
        } else if (earliestActivityDate) {
          reqStart = earliestActivityDate
        } else {
          reqStart = todayStr
        }
        if (cutoverDate && cutoverDate > reqStart) {
          reqStart = cutoverDate
        }
        const reqEnd = todayStr > reqStart ? todayStr : reqStart
        rawRanges.push({ start: reqStart, end: reqEnd })
      } else {
        // Non-shifts (bi_monthly, weekly, monthly, daily, helper)
        let effStart = startOfPrevMonth
        if (cutoverDate && cutoverDate > effStart) {
          effStart = cutoverDate
        }
        if (empHireDate && empHireDate > effStart) {
          effStart = empHireDate
        }
        rawRanges.push({ start: effStart, end: endOfNextMonth })
      }
    }
  }

  if (rawRanges.length === 0) {
    rawRanges.push({ start: startOfPrevMonth, end: endOfNextMonth })
  }

  // Сортировка по start, затем по end
  rawRanges.sort((a, b) => a.start.localeCompare(b.start) || a.end.localeCompare(b.end))

  // Слияние пересекающихся и строго примыкающих диапазонов
  const merged = []
  for (const r of rawRanges) {
    if (merged.length === 0) {
      merged.push({ start: r.start, end: r.end })
      continue
    }

    const last = merged[merged.length - 1]

    // Проверяем следующий календарный день после last.end
    const d = new Date(last.end + 'T12:00:00Z')
    d.setUTCDate(d.getUTCDate() + 1)
    const nextDay = d.toISOString().split('T')[0]

    // Overlapping (r.start <= last.end) или strictly adjacent (r.start === nextDay)
    if (r.start <= last.end || r.start === nextDay) {
      if (r.end > last.end) {
        last.end = r.end
      }
    } else {
      merged.push({ start: r.start, end: r.end })
    }
  }

  return merged
}

/**
 * Пакетный расчёт напоминаний о ближайших выплатах для дашборда (P0-1 & P0-2).
 * Устраняет N+1 сетевой fan-out: данные загружаются broad-batch по ПВЗ (5 запросов на весь ПВЗ),
 * а расчёт каждого сотрудника выполняется чисто в памяти через resolveEmployeeActualSalaryPeriodFromData.
 * СТРОГО READ-ONLY: не вызывает autoCloseExpiredShifts (DASHBOARD_AUTO_CLOSE_CALLS = 0).
 *
 * @param {string} pvzId
 * @param {Array} employees
 * @param {Object} salaryConfig
 * @param {Object} options - { configHistory, configHistoryLoadFailed, isAdmin, currentUserId, today, preloadedData, candidateScope }
 * @returns {Promise<Array>} upcomingPayouts
 */
export async function calculateUpcomingPayoutsBatch(pvzId, employees, salaryConfig, options = {}) {
  if (!pvzId || !employees || employees.length === 0 || !salaryConfig) {
    return []
  }

  const today = options.today || new Date()
  const isAdmin = options.isAdmin !== false
  const currentUserId = options.currentUserId || null
  const candidateScope = options.candidateScope || 'active'

  let targetEmployees = candidateScope === 'salary'
    ? employees
    : employees.filter(e => !e.is_legacy)

  if (!isAdmin && currentUserId) {
    targetEmployees = targetEmployees.filter(e => e.id === currentUserId)
  }

  if (targetEmployees.length === 0) {
    return []
  }

  // 1. Загружаем историю конфигурации зарплат ОДИН раз для всего ПВЗ
  let configHistory = options.configHistory || []
  let configHistoryLoadFailed = Boolean(options.configHistoryLoadFailed)

  if (!options.configHistory && !configHistoryLoadFailed) {
    try {
      const historyRes = await getSalaryConfigHistory(pvzId)
      configHistory = Array.isArray(historyRes) ? historyRes : (historyRes?.data || [])
    } catch (err) {
      configHistoryLoadFailed = true
      configHistory = []
    }
  }

  // 2. Если preloadedData уже переданы (например, в тестах) — используем их напрямую
  let sharedData = options.preloadedData || null

  if (!sharedData) {
    const todayStr = getDateInTimeZone(today, salaryConfig?.timezone)
    const cutoverDate = salaryConfig?.settlement_cutover_date || salaryConfig?.settlementCutoverDate || null

    let needsEarliestActivity = false
    for (const emp of targetEmployees) {
      if (!emp.is_legacy) {
        const freq = getEffectivePayoutFrequency(emp, salaryConfig)
        const empHireDate = emp.hired_at ? emp.hired_at.split('T')[0] : null
        if (freq === 'shifts' && !empHireDate && !cutoverDate) {
          needsEarliestActivity = true
        }
      }
    }

    let earliestActivityDate = null
    let earliestActivityLookupStatus = 'not_needed'

    if (needsEarliestActivity) {
      try {
        const eaRes = await apiClient(`/schedule/earliest-activity?pvzId=${pvzId}`)
        if (eaRes && Object.prototype.hasOwnProperty.call(eaRes, 'earliestDate')) {
          if (eaRes.earliestDate) {
            earliestActivityDate = eaRes.earliestDate
            earliestActivityLookupStatus = 'success'
          } else {
            earliestActivityLookupStatus = 'empty'
          }
        } else {
          earliestActivityLookupStatus = 'error'
        }
      } catch (err) {
        earliestActivityLookupStatus = 'error'
      }
    }

    const plannedRanges = planFactualDateRanges(targetEmployees, salaryConfig, today, earliestActivityDate)

    const rangeFetchPromises = plannedRanges.map(async (r) => {
      const paramsBroad = new URLSearchParams({ pvzId, startDate: r.start, endDate: r.end })
      const paramsPayments = new URLSearchParams({ pvzId, periodStart: r.start, periodEnd: r.end })

      const [logsRes, schedRes, payRes, dedRes] = await Promise.all([
        apiClient('/work-logs?' + paramsBroad.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
        apiClient('/schedule?' + paramsBroad.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
        apiClient('/payments?' + paramsPayments.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error })),
        apiClient('/deductions?' + paramsBroad.toString()).then(data => ({ data, error: null })).catch(error => ({ data: null, error }))
      ])

      return {
        logs: Array.isArray(logsRes.data) ? logsRes.data : (logsRes.data?.data || []),
        schedule: Array.isArray(schedRes.data) ? schedRes.data : (schedRes.data?.data || []),
        payments: Array.isArray(payRes.data) ? payRes.data : (payRes.data?.data || []),
        deductions: Array.isArray(dedRes.data) ? dedRes.data : (dedRes.data?.data || []),
        error: logsRes.error || schedRes.error || payRes.error || dedRes.error
      }
    })

    const fetchResults = await Promise.all(rangeFetchPromises)

    const rawLogs = []
    const rawSchedule = []
    const rawPayments = []
    const rawDeductions = []
    let fetchError = null

    for (const fr of fetchResults) {
      if (fr.error && !fetchError) fetchError = fr.error
      if (fr.logs) rawLogs.push(...fr.logs)
      if (fr.schedule) rawSchedule.push(...fr.schedule)
      if (fr.payments) rawPayments.push(...fr.payments)
      if (fr.deductions) rawDeductions.push(...fr.deductions)
    }

    const dedupe = (arr, keyFn) => {
      const seen = new Set()
      const res = []
      for (const item of arr) {
        if (!item) continue
        const k = keyFn(item)
        if (!seen.has(k)) {
          seen.add(k)
          res.push(item)
        }
      }
      return res
    }

    const logs = dedupe(rawLogs, x => x.id || `${x.employee_id}_${x.work_date}_${x.type}`)
    const schedule = dedupe(rawSchedule, x => x.id || `${x.employee_id}_${x.work_date}`)
    const payments = dedupe(rawPayments, x => x.id || `${x.employee_id}_${x.date || x.created_at}_${x.amount}`)
    const deductions = dedupe(rawDeductions, x => x.id || `${x.employee_id}_${x.date}_${x.amount}`)

    const totalFetchDays = plannedRanges.reduce((sum, r) => {
      const diffMs = new Date(r.end + 'T12:00:00Z') - new Date(r.start + 'T12:00:00Z')
      const days = Math.round(diffMs / (1000 * 3600 * 24)) + 1
      return sum + Math.max(1, days)
    }, 0)

    const broadStartDate = plannedRanges.length > 0 ? plannedRanges[0].start : null
    const broadEndDate = plannedRanges.length > 0
      ? plannedRanges.reduce((max, r) => (r.end > max ? r.end : max), plannedRanges[0].end)
      : null

    sharedData = {
      logs,
      schedule,
      payments,
      deductions,
      allPvzSchedule: schedule,
      earliestActivityDate,
      earliestActivityLookupStatus,
      broadStartDate,
      broadEndDate,
      plannedRanges,
      uniqueRangesCount: plannedRanges.length,
      totalFetchDays,
      error: fetchError
    }
  }

  // 3. Выполняем расчёт для каждого сотрудника через канонический in-memory резолвер
  const finalResults = []

  for (const emp of targetEmployees) {
    const actualData = await resolveEmployeeActualSalaryPeriodFromData(
      emp,
      pvzId,
      salaryConfig,
      configHistory,
      sharedData,
      {
        today,
        configHistoryLoadFailed,
        readOnly: true
      }
    )

    if (!actualData?.period) continue

    // Для архивных кандидатов (is_legacy: true):
    // Если долг закрыт (debt <= 0 && !isReview && !financialDataUnavailable) — скрывать из списка
    if (emp.is_legacy) {
      const debt = actualData.debt || 0
      const isReview = Boolean(actualData.isReviewRequired)
      const isDataUnavailable = Boolean(actualData.financialDataUnavailable || actualData.status === 'data_unavailable')
      if (debt <= 0 && !isReview && !isDataUnavailable) {
        continue
      }
    }

    const hasOpenShift = Boolean(actualData.hasOpenShift) // hasOpenShift = Boolean(salaryObj?.hasOpenShift)
    const isCompleteChunk = actualData.isCompleteChunk !== false // isCompleteChunk = salaryObj?.isCompleteChunk !== false
    const isPayable = Boolean(actualData.isPayable && isCompleteChunk && !hasOpenShift)
    const accruedDebt = actualData.accruedDebt ?? (actualData.debt || 0)
    const amount = isPayable ? Math.max(0, Math.round(actualData.debt || 0)) : 0 // amount = isPayable ? Math.max(0, Math.round(salaryObj?.periodDebt ?? salaryObj?.total ?? 0)) : 0

    let payoutDateStr = actualData.period.payoutDate || actualData.period.end
    let payoutDate = actualData.period.payoutLabel || payoutDateStr

    if (!payoutDateStr && emp.pay_day != null) {
      const customDate = getNextPayDayDate(emp.pay_day)
      if (customDate) {
        payoutDateStr = `${customDate.getFullYear()}-${String(customDate.getMonth() + 1).padStart(2, '0')}-${String(customDate.getDate()).padStart(2, '0')}`
        const dayNames = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб']
        payoutDate = `${customDate.getDate()} ${customDate.toLocaleDateString('ru-RU', { month: 'short' })} (${dayNames[customDate.getDay()]})`
      }
    }

    finalResults.push({
      id: emp.id,
      name: emp.name || emp.full_name,
      role: emp.role,
      is_legacy: Boolean(emp.is_legacy),
      isLegacy: Boolean(emp.is_legacy),
      startDate: actualData.period.start,
      endDate: actualData.period.end,
      payoutDate,
      payoutDateStr,
      periodLabel: actualData.period.label || `${actualData.period.start} – ${actualData.period.end}`,
      amount,
      isPayable,
      isCompleteChunk,
      hasOpenShift,
      accruedDebt,
      status: actualData.status,
      financialDataUnavailable: Boolean(actualData.financialDataUnavailable),
      notice: actualData.notice || null
    })
  }

  finalResults.sort((a, b) => {
    const dateA = a.payoutDateStr || ''
    const dateB = b.payoutDateStr || ''
    return dateA.localeCompare(dateB)
  })

  finalResults._sharedData = sharedData

  return finalResults
}
